// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The machine on screen (M3-32, M3-33, M3-37; 08 §1, §4.2, §5).
//
// A canvas, a HUD over it, and one `MachineRuntime` underneath that owns everything with a clock
// in it. The split is the whole design: React renders, the runtime runs, and the only traffic
// between them is a readout going up and a button press coming down.
//
// **The effect creates the runtime exactly once.** React 19 in development invokes effects twice
// on mount to surface exactly this class of bug, so the cleanup has to genuinely take the machine
// apart — the loop, the listeners, the GL context and the audio context — or the second
// invocation leaves two simulations advancing against one canvas at twice the speed.
//
// **The portrait stack is 08 §5.1**: a DMD strip, the playfield at about 72%, and a bottom band
// of two flipper zones. The bands are CSS grid rows whose fractions come from `phone.ts`, which
// is also what the touch listener tests a finger against — one source of rectangles, because the
// boundary is only *visible* on a player's very first ball and a mismatch would be invisible
// from the second ball onwards.
//
// 08 §4.2: the machine's own DMD is the primary readout, "exactly as on a real machine". The HUD
// adds only what a screen needs and a cabinet does not — ball number, the player indicator during
// multiplayer, the callout caption, the ball-save countdown and a pause button.
//
// **A game does not end at the last drain; it ends when the machine stops asking.** The last
// thing NEON NOVA does with a qualifying score is take three initials, and until they are given
// the machine sits in `highScoreEntry` doing nothing — no ball, no score change, no animation.
// Rendered as nothing, that reads as a **frozen machine**: this was found by playing a full
// game in Chrome, tilting every ball, and watching the score and the DMD stop dead with no
// control on screen and no way out on a phone at all. A cabinet does not have this problem
// because its flipper buttons are physical objects with a player's hands already on them; here
// they are two invisible rectangles, which is exactly the case 08 §4.2 means by "what a screen
// needs and a cabinet does not".
//
// **The screen-reader surface is two surfaces, not one (M3-40, NFR-025).** A score that changes
// forty times a second in a live region makes a screen reader talk continuously and drowns out
// the machine's own callouts, which NFR-022 already requires to be spoken — so the score sits in
// a *navigable* status list that never announces itself, and a separate polite line carries the
// dozen things in a game that are genuinely events. The wording of both lives in `narration.ts`,
// where it has tests; what is here is the two regions.
//
// **An interrupted game is offered back, not resumed behind the player's back (M3-39).** A
// resumable game is a replay that has not finished yet — re-running the input trace produces the
// identical game, which is why there is no snapshot format anywhere in this product. But the
// machine still starts *fresh* and instantly: 08 §1's five seconds to a plunger is the promise
// that matters most, and it must not wait on a database read. So the fresh game is built at once,
// the offer arrives beside it when the read comes back, and taking it rebuilds the machine
// deliberately — which is a thing a player asked for and can therefore afford a moment.
//
// **The end of a game is where the funnel ends (M3-38, 08 §1).** A machine that finishes a game
// and shows nothing is a dead end: `pressLaunch` will start another one, but there is no control
// on screen saying so, and the score the player just earned disappears without being
// acknowledged. 08 §1 puts the account ask exactly here — *"You scored 4,182,650. That is a real
// score."* — as loss aversion used honestly: the score exists first, and is theirs, and the offer
// is to keep it. Accounts are M4, so the control that keeps it renders **disabled and says what
// it is waiting for**, which is the same rule `routes.ts` applies to every unbuilt destination.
//
// **The device-motion offer (M3-34) is shown only while a ball is sitting in the lane.** 08 §5.2
// wants it "offered once", and where it is offered decides whether that is a courtesy or an
// interruption: a permission bar that arrives mid-ball is a modal over a moving ball, and one
// that arrives on the attract screen competes with 08 §1's single primary control on the one
// screen the whole landing design is built around. Waiting for the plunger is the only moment in
// a game with no ball moving and nothing else being asked of the player.

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import machineJson from '../../../machines/neon-nova/machine.json?raw'
import { MachineRuntime, type ControlState, type MachineReadout } from '../machineRuntime'
import {
  orientationFor,
  phoneLayout,
  stageRows,
  type Orientation,
  type PhoneLayout,
} from '../phone'
import {
  focusStartsOn,
  hasPulledPlunger,
  startingInitialsFor,
  hasSeenTouchZones,
  markPlungerPulled,
  markTouchZonesSeen,
  readMotionChoice,
  writeFocusChoice,
  writeMotionChoice,
  type MotionChoice,
} from '../firstRun'
import {
  browserMotionHost,
  motionNeedsSystemPrompt,
  requestMotionPermission,
  shouldOfferMotionNudge,
} from '../../input/motion'
import { coarsePointer, finePointer } from '../pointer'
// How many can share one machine (FR-013), and what the hot-seat chooser offers. Its own module
// because the prerenderer's `numberOfPlayers` is a claim about this control — see `seats.ts`.
import { SEATS } from '../seats'
import { batWidthFor, batsFrom } from '../cabinet'
import { CabinetBand, paintCabinetBand } from './CabinetBand'
import {
  exitFullscreen,
  isFullscreen,
  isPlayFocused,
  requestFullscreen,
  screenOnPlunge,
  setPlayFocus,
} from '../playFocus'
import { usePlayFocus } from '../usePlayFocus'
import { ScreenWake, browserWakeLock } from '../wakeLock'
import { narrate, statusFields, type MachineStatus } from '../narration'
import { unavailableReason } from '../routes'
import { BALL_UNKNOWN, LocalHistory, type InterruptedGame, type LocalScore } from '../../storage/history'
import { ReplayOutbox } from '../../storage/outbox'
import type { ReplayTrace } from '@core/replay'
import { useElementSize } from '../useViewport'
import { KeyGuide } from './KeyGuide'
import { shouldShowKeyGuide } from '../keyGuide'
import { Link, useNavigate } from 'react-router'
import { EnterHeadset } from './EnterHeadset'
import { knownTier } from '../entitlementSignal'
import { readDraft, saveDraft } from '../activeMachine'
import { draftAutosave } from '../draftAutosave'
import { VoiceControls } from './VoiceControls'
import { SoundControl } from './SoundControl'
import { ReadAloud } from './ReadAloud'
import { joinIdOf } from '../account/invites'
import type { AloudSnapshot } from '../machineAloud'
import { markOnboarding } from '../onboarding'
import { StageMenu, stageMenuRows } from './StageMenu'
import { ReportMachine, isReportable } from './ReportMachine'
import { Scoreboard, seatRows, standingsRows } from './Scoreboard'
import type { OnlineGameView } from '../../net/gameDoc'
import { TRACE_INTERVAL_MS } from '../../net/traceStream'
import { MIC_BLOCKED_NOTICE, shouldTellAboutMicrophone } from '../../audio/voiceMute'
import { useLocalisation } from '../i18n/useLanguage'
import type { MessageKey, Translator } from '../i18n'
// The catalogue, not the account bundle. `gateVerdict` lives beside the table it asks about
// precisely so the front door may read it without reaching anything that could open a connection
// (FR-030) — see the note at the top of `pricingCatalog.ts`.
import { gateVerdict, type Tier } from '@src/ui/pricingCatalog'

// React invokes this import only when an explicit Arcade action renders the open dialog.
const MachineGalleryDialog = lazy(async () => ({
  default: (await import('./MachineGallery')).MachineGalleryDialog,
}))

const IDLE: MachineReadout = {
  score: 0,
  ball: 1,
  ballsPerGame: 3,
  player: 1,
  players: 1,
  phase: 'attract',
  awaitingLaunch: false,
  plunger: 0,
  tilted: false,
  ballSaveSeconds: 0,
  initials: 'AAA',
  initialsAt: -1,
  caption: '',
  notice: '',
  // True until a machine says otherwise: the idle stage is NEON NOVA, which has a DMD. Starting
  // false would print a HUD score for the frames before the first readout and then take it away.
  hasDisplay: true,
  audible: false,
  audioMuted: false,
  paused: false,
  micMuted: false,
  voiceMuted: false,
  micAccess: 'unknown',
  micNoticed: false,
  standings: [{ player: 1, score: 0, tilted: false, ballsPlayed: 0 }],
}

export interface MachineStageProps {
  /**
   * Start in attract mode rather than with a ball up.
   *
   * True on the landing route: 08 §1 wants "NEON NOVA in attract mode, lights running" and then a
   * single primary control the visitor presses themselves. A machine that had already started a
   * game for them has spent their first ball while they were still reading.
   */
  readonly attract?: boolean
  /**
   * The machine to run, or nothing for the one in the bundle (G1-01).
   *
   * **The default matters as much as the prop.** The landing route and every offline path must keep
   * reaching no backend at all — FR-030 and NFR-006 both rest on it and `check:anonymous` is what
   * says so mechanically — so this stage still runs with no argument, out of bytes that shipped with
   * the page. What the prop adds is that a *second* machine can be run at all: until G1 the two
   * routes that start a live game were both wired to this one document, so the Builder could publish
   * a machine, the server would serve it, the leaderboard would rank it, and nothing could open it.
   *
   * Already validated by the time it arrives. `machineSource.ts` refuses a document this build
   * cannot run and names the class that refused it; a stage that revalidated would be a second
   * opinion, and the two would eventually differ.
   */
  readonly machine?: unknown
  /**
   * The page’s one top-level heading (S3-04).
   *
   * **Screen-reader only, and that is the point.** This route is a machine: 08 §1 gives the
   * landing page no hero copy at all, and a visible heading over the cabinet is exactly the
   * paragraph that decision refuses. But a document with no `h1` is a document with no subject,
   * and until this existed the only top-level heading on the front door was the navigation bar’s
   * own label.
   *
   * The caller supplies it because the two callers mean different things: the front door is a
   * page about the product, and `/play/{machine}` is a page about that machine.
   */
  readonly heading?: string
  /**
   * Nothing about this game is written down (M9-32).
   *
   * **The Builder's draft, and only that.** A published version is immutable and a leaderboard
   * score is a claim about its exact bytes — the server re-simulates the trace against them — so a
   * machine that changes under the player between one game and the next has nothing a board could
   * check. It has nothing this *browser* could check either: a draft opened from the flagship
   * carries `machineId: neon-nova` until its author publishes, so its scores would land on NEON
   * NOVA's own local table, under NEON NOVA's name, and be offered back as a resume of a game that
   * no longer exists.
   *
   * **Acted on by not opening the stores at all**, rather than by remembering not to write to them
   * at four call sites. The runtime's `history` and `outbox` are providers that may answer nothing —
   * `remember()` returns on the first of them — which is the same shape the Builder's in-panel test
   * play has always had, and the reason this needed no change below the component.
   */
  readonly unscored?: boolean
  /**
   * A control the host puts in the stage's chrome (M9-32).
   *
   * Handed in rather than built here, because *which machine is active* is the front door's
   * question and no other route has one. `/play/:machineId` names a machine and must keep naming
   * it — `machineSource.ts` says why at length — so this prop is absent there and the switch does
   * not exist.
   */
  readonly switcher?: React.ReactNode
  /** Open the host's picture-card gallery without navigating away from its running stage. */
  readonly onBrowseMachines?: () => void
}

export function MachineStage({
  attract = false,
  machine,
  heading,
  unscored = false,
  switcher,
  onBrowseMachines,
}: MachineStageProps): React.JSX.Element {
  const { t, locale } = useLocalisation()
  // The runtime lives across React renders. Reading the latest host callback must not rebuild
  // its game or retain the callback from whichever card was selected at mount.
  const browseMachinesRef = useRef(onBrowseMachines)
  const [galleryOpen, setGalleryOpen] = useState(false)
  const galleryResume = useRef(false)
  browseMachinesRef.current = onBrowseMachines ?? (() => {
    const runtime = runtimeRef.current
    if (runtime?.openMachineGallery((id) => { void navigate('/play/' + encodeURIComponent(id)) })) return
    galleryResume.current = runtime !== null && !runtime.frozen
    if (galleryResume.current) runtime?.setPaused(true)
    setGalleryOpen(true)
  })
  /**
   * The document this stage is running, and the two facts read out of it.
   *
   * Parsed once per machine rather than once per module, which is what G1-01 changed: `MACHINE` and
   * `BATS` were module-level constants over the bundled bytes, so a stage handed another machine
   * would have keyed its history to NEON NOVA's id and drawn NEON NOVA's bats under it. The id and
   * the version are what the local history is keyed and gated on — a trace recorded against another
   * version is not offered back, because re-running it would produce a different game from the one
   * the player left.
   */
  const document_ = useMemo(() => machine ?? (JSON.parse(machineJson) as unknown), [machine])
  const identity = useMemo(
    () => document_ as { machineId: string; machineVersion: string },
    [document_],
  )
  /**
   * What this machine is called, for the accessible name and the heading.
   *
   * The name was the literal string `NEON NOVA` on this section until S3-04, so a screen reader
   * announced the flagship’s name over every machine the Builder ever published.
   */
  const machineTitle = useMemo(() => {
    const meta = (document_ as { meta?: { title?: unknown } }).meta
    const title = typeof meta?.title === 'string' ? meta.title.trim() : ''
    return title === '' ? identity.machineId : title
  }, [document_, identity])
  const bats = useMemo(() => batsFrom(document_), [document_])
  /**
   * Whether this machine can be reported, and whether the panel is open (M9-28, 11 §6).
   *
   * Read from the document rather than from the route, because the route does not know: `/` and
   * `/play/:machineId` both draw whatever definition they were handed, and what decides is
   * `meta.author.kind`. A first-party machine is not offered the control at all.
   */
  const reportable = useMemo(() => isReportable(document_), [document_])
  const [reporting, setReporting] = useState(false)
  const stageRef = useRef<HTMLElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const dmdRef = useRef<HTMLCanvasElement | null>(null)
  const runtimeRef = useRef<MachineRuntime | null>(null)
  const [readout, setReadout] = useState<MachineReadout>(IDLE)
  const [running, setRunning] = useState(false)
  const [renderError, setRenderError] = useState<string | null>(null)
  // Shown for the first ball of the first game on this browser, then never again (08 §5.2).
  const [showZones, setShowZones] = useState(() => !hasSeenTouchZones())
  /**
   * Whether to show the arrow that teaches the pull (M3-51).
   *
   * Its own state and its own storage key, for the reason `firstRun.ts` gives: a thumb finds the
   * flippers in seconds and nobody guesses that the ball on the right has to be *dragged*.
   */
  const [showPull, setShowPull] = useState(() => !hasPulledPlunger())
  /** An invite link that could not be taken up — see the effect that sets it (G3-05). */
  const [inviteRefused, setInviteRefused] = useState(false)
  // The player's answer to the motion offer, and whether the sensor has since proved it works.
  const [motion, setMotion] = useState<MotionChoice | undefined>(() => readMotionChoice())
  const [motionRefused, setMotionRefused] = useState(false)
  const [motionFelt, setMotionFelt] = useState(false)
  /**
   * Has the "motion nudging is on" line had its say? (Bug: it never went away.)
   *
   * The line used to be tied to `motionFelt` alone, so it stood until the sensor produced a shove
   * — and on a desktop, in an emulated device, or on a phone whose sensor is granted but silent,
   * that never happens. A permanent banner across the top strip is worse than an unproven hint:
   * it suppresses the key guide (`askIsUp`) for the whole session and reads as a machine stuck
   * mid-sentence. So it is *also* capped by a clock, exactly as the refusal line is.
   */
  const [motionHintOver, setMotionHintOver] = useState(false)
  // Resolved once, at mount. Neither answer changes while a machine is on screen: a browser does
  // not grow a sensor, and a phone does not become a desktop.
  const [motionHost] = useState(browserMotionHost)
  // Focus mode (M3-45). The signal is the authority — `AppShell` reads the same one — and this is
  // the copy React re-renders from, read the way the shell reads it (M8-72): a `useState` here and
  // a subscription in an effect leave a window between the two that a mount-time set falls into.
  const focused = usePlayFocus()
  /**
   * The best score on this browser before the game now on screen (M3-48).
   *
   * **A ref, and updated only while a game is unfinished.** `table` is re-read *after* a game ends,
   * so by the time the end panel renders it already contains the score being judged — comparing
   * against it would tell every player they had tied their own best, every time. Holding the
   * pre-game value is the only way to answer "is this better than what you had".
   */
  const bestBefore = useRef(0)
  // The browser's memory of your games (M3-39). Opened once; `undefined` until it answers, and
  // a history that could not open is a machine that plays identically and forgets.
  // Always hand the screen back on the way out — an unmounted stage that left the shell collapsed
  // would be an arcade list with no navigation on it. This component's own copy of the signal is
  // `focused` above, through `usePlayFocus`; nothing here subscribes by hand any more (M8-72),
  // because a subscription made in this effect began *after* the set two lines below it, and the
  // shell's — a parent's, so later still — missed that set entirely.
  useEffect(() => {
    // **The choice the player last made, applied on arrival** (M3-45). `focusStartsOn` is false
    // for anybody who has never chosen it — see the argument there about a first-time visitor
    // landing on a machine with no navigation — so this only ever restores a state somebody asked
    // for. The fullscreen half is not asked for here and cannot be: a mount is not a user gesture.
    // `onPlunge` collects it on the first pull that is.
    if (focusStartsOn()) setPlayFocus(true)
    return () => {
      setPlayFocus(false)
      void exitFullscreen()
    }
  }, [])

  /**
   * The screen stays awake while a machine is running (M3-41).
   *
   * **Gated on `running` rather than held for the page.** A wake lock is a battery cost and a
   * promise to the person holding the device, so it exists while there is a machine on the glass
   * and not on the arcade list, not on Settings, and not while the tab is in the background —
   * `ScreenWake` handles that last one itself, because the platform drops the lock when a document
   * hides and does not give it back.
   */
  useEffect(() => {
    if (!running) return
    const env = browserWakeLock()
    if (env === undefined) return
    const wake = new ScreenWake(env)
    wake.request()
    return () => wake.dispose()
  }, [running])

  const [history, setHistory] = useState<LocalHistory | undefined>(undefined)
  // …and again as a ref, which is what the runtime is given. The state drives the *page* — the
  // table, the "kept on this device" line — and the ref keeps the machine out of it: a value in
  // the effect's dependencies would rebuild the whole machine the moment the database answered.
  const historyRef = useRef<LocalHistory | undefined>(undefined)

  // **Games this browser has finished and not yet posted.** Until M7-20 a finished game was written
  // down here and posted nowhere — the only thing that ever called `submitReplay` was the manual
  // migration panel, so a player who finished a game and closed the tab had a score no leaderboard
  // ever heard about.
  //
  // **Written here and emptied elsewhere.** This page must not so much as import the thing that
  // posts: reaching the API client from the play path puts the Supabase SDK on the anonymous
  // funnel, and `check-anonymous.mjs` fails the build for it — rightly, because constructing that
  // client reads storage, refreshes a session and can mint an identity, and FR-030 wants a game
  // played by a stranger to appear in no log at all. `useReplaySync`, in the account chunk, does
  // the posting when a signed-in player next opens an account page. This side is IndexedDB and the
  // core, and it works with no network and no account.
  const outboxRef = useRef<ReplayOutbox | undefined>(undefined)
  const [offered, setOffered] = useState<InterruptedGame | undefined>(undefined)
  /** The trace the machine is actually being rebuilt around, once the player has said yes. */
  const [resumeFrom, setResumeFrom] = useState<ReplayTrace | undefined>(undefined)
  const [table, setTable] = useState<readonly LocalScore[]>([])
  /** The last thing worth saying out loud, and what the machine was doing when it was said. */
  const [spoken, setSpoken] = useState('')
  /** Whether a headset session is running, and the one sentence XR has to say (M5). */
  const [presentingXr, setPresentingXr] = useState(false)
  const [xrProblem, setXrProblem] = useState<string | null>(null)
  const [xrToast, setXrToast] = useState('')
  const [AccountUtility,setAccountUtility]=useState<React.ComponentType<{utility?: boolean}>|null>(null)
  /**
   * The shell's own one-line acknowledgement (M3-55).
   *
   * **Separate from `xrToast`, because that one is cleared every frame.** The XR report is
   * published on the same rhythm as the score and assigns `report?.toast ?? ''` whether or not a
   * headset is presenting — so on a flat screen it writes an empty string on the very next
   * readout. Ending a game therefore said nothing at all, which is precisely what the effect
   * below was written to prevent. It went unnoticed because `endGame` could not be reached
   * without a headset until this menu existed.
   */
  const [shellNotice, setShellNotice] = useState('')
  /**
   * A shared arcade was actually joined, so there is a voice to mute (M8-09).
   *
   * **Latched for the life of this machine rather than cleared on the way out of the headset.**
   * Leaving immersive is usually an interruption — a system menu, a headset lifted off — and
   * `arcadePresence` holds the membership open for four seconds precisely because the player is
   * probably coming back. Hiding the mute controls the instant a session ends would take them
   * away in the one moment somebody is most likely to want them: headset off, room still live,
   * microphone still open. After the membership does close, the controls are still honest —
   * `setVoiceMute` records the answer and the next join starts with it.
   */
  const [inArcade, setInArcade] = useState(false)
  /**
   * How many are sharing this machine (M8-17, FR-013).
   *
   * **Part of the runtime's identity, not a setting it can be told about.** A game's player count
   * is fixed when the game starts — the core builds one `PlayerState` per seat and the rule engine
   * sizes its per-player variables from it — so changing it is starting a different game, and the
   * effect below rebuilds for exactly that reason. The chooser is only offered in attract, where
   * there is no game to throw away.
   */
  const [players, setPlayers] = useState(1)
  /**
   * The online game this player is in, or none (M8-14 … M8-16).
   *
   * Held as state rather than read on demand so the scoreboard redraws when somebody else's ball
   * ends. The document itself lives in the account chunk's session; this is a copy for rendering,
   * and it is only ever replaced wholesale — the same rule the session follows about never
   * assembling a document from the changes announced to it.
   */
  const [online, setOnline] = useState<OnlineGameView | undefined>(undefined)
  const [joining, setJoining] = useState(false)
  /**
   * The headset asked to end the game (M5-24, M5-25).
   *
   * A flag rather than an immediate teardown, because the confirmation has already happened —
   * the wrist menu armed and confirmed it — and what is left is the same thing "Start fresh"
   * does: forget the interrupted game and offer a new one. Doing it through the render path
   * rather than from inside a frame callback keeps React's state the only owner of what is shown.
   */
  const [endRequested, setEndRequested] = useState(false)
  const navigate = useNavigate()
  const saidFor = useRef<MachineStatus | undefined>(undefined)
  const offerable = shouldOfferMotionNudge(motionHost, coarsePointer())
  const systemPrompt = motionNeedsSystemPrompt(motionHost)

  // The **stage**, not the window: the shell has a top bar and, on a phone, a navigation bar, so
  // laying the machine out against the viewport would put every band a bar's height off. The
  // runtime measures the same element on every touch, so what is drawn and what is touched are
  // the same rectangles.
  const [stage, setStage] = useState<HTMLElement | null>(null)
  const box = useElementSize(stage)
  /**
   * The HUD, measured, so the key guide knows how much of the pane is already spoken for (M3-56).
   *
   * **Measured rather than assumed**, because its height is a function of its contents: the score,
   * the ball line, and a player indicator that only exists in a multiplayer game. A reserve fitted
   * to one window is a rail that slides under the ball number on the next one, which is what the
   * first two attempts at this did.
   *
   * Cheap despite the observer: `stage__hud` is `left: 0; right: 0`, so its width is the pane's and
   * never moves, its height changes only when a line wraps, and `useElementSize` does not set state
   * for a box that has not changed. A score ticking over is not a resize.
   */
  const [hud, setHud] = useState<HTMLElement | null>(null)
  const hudBox = useElementSize(hud)
  const orientation = orientationFor(box.width, box.height)
  // The same rectangles the touch listener is tested against, which is the whole point of
  // `phone.ts`: the band is now *drawn* as buttons, so any disagreement between the two would be
  // a button in one place and its hit area in another.
  const layout = phoneLayout(box.width, box.height)

  // Read once, like Settings does. This gates whether the overlay's plunger button is redundant —
  // `coarsePointer` and not `touchCapable`, because a desktop with a touchscreen is still driven
  // by a mouse, and a mouse fires no `touchstart` and so can never reach the cabinet's plunger.
  const [touchPrimary] = useState(coarsePointer)
  // What this browser was last told it is (M10-12). Read once, like the pointer above: the end-of-
  // game offer asks it, this component re-renders on every frame a score moves, and `knownTier`
  // reads `localStorage`. See `endOfGameOffer` for why a cold tab is treated as a visitor.
  const [tier] = useState(knownTier)
  /**
   * Does this browser already hold a machine of its own? (M10-25, widened M10-30.)
   *
   * **Asked of every tier now, and only ever locally.** It decided one thing when it was written —
   * whether the end of a game offers a Pro player the Builder — and the probe was guarded on
   * `tier === 'pro'` under a comment about not opening a store for the tiers that would not use the
   * answer. M10-30 gave the other two a quiet door of their own, so all three use it, and the guard
   * would now be the reason a visitor's aside never retires. A player who has made something is
   * offered nothing, on every tier, which is what stops either door being a nag.
   *
   * **The read costs nothing new on the front door.** `Landing` calls `useActiveMachine` on the
   * same mount, and that calls this same `readDraft` unconditionally for every visitor — so the
   * store has been opened for strangers since M9-32 and this is a second read of it rather than a
   * first open. `check-anonymous` walks the module graph for anything that could reach a network
   * and `readDraft` was already imported here, so the funnel is unchanged: still zero requests.
   *
   * Starts `true`, which is the silent answer. A brief flash of an offer while the disk is read
   * would be the interstitial the whole panel is written to avoid, and the wrong direction to err
   * in is the one that advertises.
   */
  const [built, setBuilt] = useState(true)
  useEffect(() => {
    let alive = true
    void readDraft().then((held) => {
      if (alive) setBuilt(held !== undefined)
    })
    return () => {
      alive = false
    }
  }, [])
  // **The third pointer question** (M3-56), and deliberately not `!touchPrimary`: a television
  // browser reports `pointer: none` and has no keyboard to be given a legend for. Read once for
  // the same reason as above — a media query re-asked on every render is a render that can change
  // what is on screen because a window was moved between two monitors.
  const [pointerIsFine] = useState(finePointer)

  /**
   * The cabinet band, written to directly (M3-49).
   *
   * **Not React state**, because a bat goes up and down several times a second and the knob
   * follows a thumb continuously: routing either through `setState` re-renders this component —
   * which is a large one — at the rate a hand moves. The runtime hands over a `ControlState` only
   * when something changed and this writes two classes and a custom property.
   */
  const bandRef = useRef<HTMLDivElement | null>(null)
  const paintControls = useCallback((state: ControlState) => {
    paintCabinetBand(bandRef.current, state)
  }, [])

  // Opened alongside the machine rather than before it: a database read must never be the
  // reason a stranger is still looking at a skeleton cabinet.
  useEffect(() => {
    // **A draft opens neither store** (M9-32). Not "opens them and does not write": the refs stay
    // undefined, so the runtime's providers answer nothing and `remember()` returns on its first
    // line — and the table and the resume offer, which are *reads* keyed by a machine id this draft
    // does not really own, never happen either. See `unscored`.
    if (unscored) return undefined
    let live = true
    void ReplayOutbox.open().then((queue) => {
      if (live) outboxRef.current = queue
    })
    void LocalHistory.open().then(async (opened) => {
      if (!live) return
      historyRef.current = opened
      setHistory(opened)
      setTable(await opened.scores(identity.machineId))
      const waiting = await opened.resumable(identity.machineId, identity.machineVersion)
      if (live) setOffered(waiting)
    })
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const stage = stageRef.current
    if (canvas === null || stage === null) return

    // Read once and held, so the option below cannot ask twice and get two answers.
    const openOn = startingInitialsFor()

    // The bench's draft, kept as the Builder keeps its own (M13-16): 1200 ms after the last
    // commit, only once something has changed, and flushed on the way down.
    const autosave = draftAutosave({ save: (definition) => saveDraft(definition) })

    const runtime = new MachineRuntime({
      machine: document_,
      canvas,
      dmdCanvas: dmdRef.current,
      touchSurface: stage,
      // Measured off the stage element rather than the window: the shell has a top bar and, on a
      // phone, a navigation bar, so the machine's pane is not the viewport and the flipper band
      // would sit a bar's height below where a thumb was told it is.
      layout: () => {
        const box = stage.getBoundingClientRect()
        return phoneLayout(box.width, box.height)
      },
      keyboard: window,
      attract,
      playerCount: players,
      // 08 §5.1's portrait default. Follow brings the action to the middle of a pane that is 72%
      // of a phone screen and would otherwise put the top of the playfield out of reading range.
      camera: phoneLayout(stage.clientWidth, stage.clientHeight).orientation === 'portrait'
        ? 'follow'
        : 'player',
      // The drawn cabinet, painted straight onto the band. Deliberately not part of the readout —
      // see `onControls` on the runtime for what that would cost.
      onControls: paintControls,
      onReadout: (next: MachineReadout) => {
        setReadout(next)
        // The headset's one sentence, and whether a session is running, published on the same
        // rhythm as the score — at most once a frame, and only when something changed. A second
        // subscription for two strings would be a second re-render path through the same tree.
        const runtime = runtimeRef.current
        const report = runtime?.xrFrameReport
        setPresentingXr(runtime?.presentingXr === true)
        setXrToast(report?.toast ?? '')
      },
      onRenderFailure: setRenderError,
      // **The mute state crosses the FR-030 boundary here and nowhere else** (M8-09). The runtime
      // owns the two booleans and cannot reach a microphone from the front door; this is the same
      // dynamic import the arcade itself goes through, and it fails the same way — silently, into
      // a build with no backend, where there is nothing to mute and nothing to say about it.
      onVoiceMute: (state) => {
        void import('../account')
          .then((mod) => mod.setVoiceMute(state))
          .catch(() => undefined)
      },
      // 07 §7's outbound voice gate (M8-13). Same boundary and the same silence on failure: a
      // build with no backend has no microphone to gate, and nothing to say about it.
      onVoiceGate: (closed) => {
        void import('../account')
          .then((mod) => mod.setVoiceGate(closed))
          .catch(() => undefined)
      },
      // The headset's Keep (M13-20). The same crossing, at the press: the account chunk answers
      // with its client and whether there is a session, and a build with no backend answers with
      // nothing — which the wrist row says as "Keep needs the arcade".
      // Every commit at the bench, with whether the bench is still the machine it entered on.
      onDraft: (definition, edited) => autosave.changed(definition, edited),
      keep: () =>
        import('../account')
          .then((mod) => mod.keepBackend())
          .catch(() => undefined),
      // The two wrist-menu rows the machine cannot perform on itself (M5-24). Ending a game is
      // unmounting this machine and keeping what it wrote; changing machine is a navigation.
      onXrMenu: (selection) => {
        if (selection.item === 'endGame') setEndRequested(true)
        else if (selection.item === 'otherMachine') {
          if (browseMachinesRef.current !== undefined) browseMachinesRef.current()
          else void navigate('/arcade')
        }
        else if (selection.item === 'account') {
          // Explicit account intent crosses FR-030 here. Keep this stage/runtime mounted so
          // its draft, undo stack and suspended move survive sign-in and cancellation.
          void import('../account').then(mod=>setAccountUtility(()=>mod.AccountRoute))
            .catch(()=>setXrProblem('Account could not open. Your table and draft are kept.'))
        }
      },
      onFirstFlip: () => {
        markTouchZonesSeen()
        setShowZones(false)
      },
      // The arrow has done its job the moment the gesture has been done once (M3-51). Nothing
      // about the screen happens here any more — see `onPlunge` below, which is every pull.
      onFirstPlunge: () => {
        markPlungerPulled()
        setShowPull(false)
      },
      /**
       * A finger pulled the plunger, so the machine takes the screen (M3-45, M8-73).
       *
       * **Every pull, not the first.** Pulling the plunger is the player saying they are playing
       * now, and that is true of every ball rather than of one moment in a browser's history. It is
       * also the first gesture on the critical path — about a second after the machine appears —
       * and the one nobody experiences as an interruption, because the thing they just did was ask
       * for a ball and the machine getting bigger is the answer to it.
       *
       * **This is the finger path and nothing else reaches it.** `touch.ts` binds `touchstart` and
       * `touchend`, which a mouse never fires, so a desktop plays on with its bars — where they
       * cost 56 px of a tall window rather than thirty-one per cent of a short one.
       *
       * **Nothing stops it, and both halves are asked for every time** (M8-73, decided 2 Sep
       * 2026). A stored "off" used to stop the rows collapsing and fullscreen used to be asked once
       * a visit; `screenOnPlunge` records what that cost a player. Both calls sit inside the
       * `touchend` listener, which is the user gesture `requestFullscreen` needs, and a browser
       * that refuses — every iPhone — refuses silently while the rows collapse anyway.
       */
      onPlunge: () => {
        const want = screenOnPlunge({ focused: isPlayFocused(), fullscreen: isFullscreen() })
        if (want.focus) setPlayFocus(true)
        if (want.fullscreen) void requestFullscreen(stageRef.current ?? undefined)
      },
      // Read once, here, rather than passed as a prop: the runtime is built once and the stored
      // answer is what it was at mount. A change made *during* this machine's life goes through
      // `setMotionEnabled` below, which is why that method exists.
      // Where this player's initials entry opens (M2-19a). Read once, at construction, like the
      // motion answer below and for the same reason: it is a setting the game is built with, and a
      // value that changed mid-game would put a header on the trace the simulation never used.
      ...(openOn === undefined ? {} : { startingInitials: openOn }),
      motionNudge: readMotionChoice() === 'on',
      onMotionShove: () => setMotionFelt(true),
      history: () => historyRef.current,
      // A provider, like the history and for the same reason: the queue opens asynchronously and
      // building a machine must not wait for it. A game finished in the second before it opens is
      // the one case this loses, and it loses it to the local table rather than into nothing.
      outbox: () => outboxRef.current,
      ...(resumeFrom === undefined ? {} : { resumeFrom }),
      onSaved: () => {
        void historyRef.current?.scores(identity.machineId).then(setTable)
        // The fourth step, and the only one of the four that is a genuine event rather than a
        // state (G2-11). Marked where the score is actually written down, not where a game ends:
        // a game that ended and was not recorded has not taught anybody that scores are kept.
        noteStep('saveScore')
      },
    })
    runtimeRef.current = runtime
    setRunning(true)

    return () => {
      // The last commit goes to the draft before the runtime goes: a page leaving mid-session
      // is the case the draft exists for.
      autosave.dispose()
      runtime.dispose()
      runtimeRef.current = null
      setRunning(false)
    }
    // Built from `attract`, an accepted resume and the player count, and from nothing else. The
    // player count belongs here because it is part of what the machine *is*: the core sizes its
    // per-player state from it at construction, so a four-player game is a different machine from
    // a solo one rather than the same one told something. Adding the
    // callbacks here would rebuild the whole machine whenever React re-created one of them,
    // mid-ball; adding the history would rebuild it the moment the database answered, which is
    // a game that restarts itself a second after the player pressed the plunger. A resume is the
    // one rebuild that is *wanted*, because a player asked for it.
  }, [attract, resumeFrom, players])

  /**
   * Fill the arcade while a headset session is running (M8-01 … M8-05).
   *
   * **The dynamic import is the FR-030 boundary**, and it is the same one `main.tsx` uses for the
   * account routes: `check-anonymous.mjs` stops its walk at `src/client/account/index.ts`, so a
   * visitor who never puts a headset on downloads none of the socket, the roster or the pose
   * stream. Loading it here rather than at start-up is also just correct — a player entering the
   * shared arcade is by definition a player with an account.
   *
   * Failure is silent on purpose. No backend, not signed in, no handle yet, the socket refused:
   * none of them is a reason to interrupt somebody who is holding a pinball machine, and every one
   * of them ends with an empty room, which is what the arcade was for the whole of M5.
   */
  useEffect(() => {
    const runtime = runtimeRef.current
    if (runtime === null) return
    let alive = true
    if (presentingXr) {
      void import('../account')
        .then(async (mod) => {
          if (!alive) return
          // **The voice controls appear because a room was actually joined**, not because a
          // headset went on. A build with no backend, a player with no handle and a refused
          // socket all end here with `false`, and all three would otherwise be offered a mute
          // button that mutes nothing.
          if (await mod.enterArcade(runtime)) setInArcade(true)
        })
        .catch(() => undefined)
    }
    return () => {
      alive = false
      // **Two different exits, and only one of them is a decision** (M8-06). This cleanup runs both
      // when the headset session ends — which on a Quest happens every time the system menu opens
      // — and when the component unmounts. The first is usually an interruption the player is
      // about to return from, so the teardown is held for four seconds and cancelled if they do.
      // The second is a page that has gone, and a socket held open for four seconds after it is a
      // row in a shared room belonging to nobody.
      void import('../account')
        .then((mod) => mod.leaveArcade(runtime))
        .catch(() => undefined)
    }
  }, [presentingXr])

  /**
   * The exit that is not an interruption.
   *
   * A separate effect with no dependencies, because a cleanup cannot tell why it is running: the
   * one above fires both when the headset session ends and when this component goes away, and
   * those need opposite answers. This one only ever fires on unmount, so it is allowed to close
   * the socket on the spot — and it cancels the held teardown the other one just scheduled,
   * whichever order React runs them in.
   */
  useEffect(() => () => {
    const runtime = runtimeRef.current
    if (runtime === null) return
    // Same gate, same reason (M11-01): leaving an arcade nobody joined is a 55 KB download on the
    // way out of a page. Joining one needs Pro, which needs an account, which sets the tier.
    if (knownTier() === undefined) return
    void import('../account')
      .then((mod) => mod.leaveArcade(runtime, { immediate: true }))
      .catch(() => undefined)
  }, [])

  /**
   * Watch the online game, for as long as this machine is on screen (M8-17).
   *
   * Subscribed once and never on the runtime's rebuild: the game outlives the machine — a player
   * choosing three seats or resuming an interrupted game does not leave the room — and a
   * subscription that came and went with the runtime would drop a notification on every rebuild.
   */
  /*
    **Watched only by somebody who could have a game to watch** (M11-01).

    This effect ran on every mount and imported the account chunk to do it — so a visitor who has
    never signed in downloaded and parsed ~55 KB of Supabase SDK **before touching anything**, to
    subscribe to online games they cannot have. Measured at 171 ms on a cold front-door load.
    `check-anonymous.mjs` said the only way to download that chunk was to navigate to `/account`;
    that was true of the module graph and untrue of the running page, because a *dynamic* import is
    invisible to a static walk.

    The gate is `knownTier()` — a `localStorage` read with no Supabase in it, written when
    `/entitlement` answers and cleared on sign-out. `undefined` means this browser has never
    resolved an entitlement, which means nobody has signed in on it, which means there is no online
    game to watch and no arcade to leave.

    **It is a hint, and it only ever costs a feature nobody could use.** A wrong `undefined` skips a
    subscription for somebody with no session; a wrong value loads a chunk a moment early. Neither
    is a correctness question, which is exactly why a stored hint is the right instrument here and
    the wrong one for anything the server enforces.
  */
  useEffect(() => {
    if (knownTier() === undefined) return
    let alive = true
    let stop: (() => void) | undefined
    void import('../account')
      .then((mod) => {
        if (!alive) return
        stop = mod.watchGame((game) => {
          if (alive) setOnline(game)
        })
      })
      .catch(() => undefined)
    return () => {
      alive = false
      stop?.()
    }
  }, [])

  /**
   * Open an online game on this machine, or leave the one this player is in (M8-17).
   *
   * Silent about every reason it might not — no backend, not signed in, no profile yet. Each of
   * them ends the same way: the player plays alone, which is what they were doing a moment ago,
   * and an error about a feature they did not ask for would be the machine complaining at them.
   */
  /**
   * Take an invite (G3-05).
   *
   * `/play/<machine>?join=<gameId>` is what `inviteUrl` builds, and this is the other end of it.
   * Attempted once, on arrival, and never retried: a link to a game that has ended is a link
   * somebody sent an hour ago, and the honest answer is the sentence below rather than a client
   * that keeps asking.
   *
   * **It says so when it fails**, which is the M8-63 rule from the other side. A join that silently
   * did nothing would leave a player who followed an invitation looking at an ordinary solo machine
   * with no idea why — and "the game has ended" is a thing they can act on by asking for another.
   */
  useEffect(() => {
    const gameId = joinIdOf(globalThis.location?.search ?? '')
    if (gameId === undefined) return
    let alive = true
    void import('../account')
      .then((mod) => mod.joinGame(gameId))
      .then((joined) => {
        if (!alive) return
        if (joined === undefined) setInviteRefused(true)
      })
      .catch(() => {
        if (alive) setInviteRefused(true)
      })
    return () => {
      alive = false
    }
  }, [])

  const hostOnline = useCallback(() => {
    setJoining(true)
    void import('../account')
      .then((mod) => mod.hostGame(identity.machineId, identity.machineVersion))
      .catch(() => undefined)
      .finally(() => setJoining(false))
  }, [])

  const leaveOnline = useCallback(() => {
    void import('../account')
      .then((mod) => mod.leaveGame())
      .catch(() => undefined)
  }, [])

  const startOnline = useCallback(() => {
    void import('../account')
      .then((mod) => mod.sendGameCommand('start'))
      .catch(() => undefined)
  }, [])

  /**
   * Tell the room when this player's ball drains (M8-17).
   *
   * **The local machine is the authority for the local ball, and the server is the authority for
   * whose ball it is.** Those are different questions and neither answers the other: the seat model
   * cannot know a ball drained, and the client cannot decide it is somebody else's turn. So the
   * ball number this browser reports is watched, and each *new* drain sends one `endBall`.
   *
   * A ref rather than state for what has been reported, because the effect must not re-run when it
   * changes — and a ball counted twice is a player who loses one they never played, which is the
   * exact failure M8-15's idempotency exists for a *different* cause of.
   */
  const reportedBall = useRef(0)
  useEffect(() => {
    // A new game is a new count. Keyed on the id rather than the object, which is replaced whole
    // on every notification.
    reportedBall.current = 0
  }, [online?.game.gameId])

  useEffect(() => {
    if (online === undefined || !online.myTurn) return
    // `ball` is the one *in play*, so the one that just ended is the one before it — except at the
    // end of a game, where there is no next ball to count from.
    const ended = readout.phase === 'gameOver' ? readout.ballsPerGame : readout.ball - 1
    if (ended <= reportedBall.current) return
    reportedBall.current = ended
    void import('../account')
      .then((mod) => mod.sendGameCommand('endBall', { score: readout.score }))
      .catch(() => undefined)
    // `readout` is rebuilt every publish; the two fields below are what this reacts to, and adding
    // the whole object would send a command on every score change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readout.ball, readout.phase, online])

  /**
   * Play the room's current ball on this machine (M8-19).
   *
   * **This is what standing at a machine in an arcade is**, and it is why the trace is inputs
   * rather than positions: the packets are re-simulated by this client's own core, so what is on
   * the glass is the player's ball frame-for-frame rather than an interpolation of where their
   * ball was a moment ago.
   *
   * The runtime is driven from inside the subscription rather than from a re-render, because this
   * arrives four times a second and a component that re-rendered on each one would be repainting
   * the whole shell to move a ball the canvas is already moving. Only the caption is state, and it
   * changes when the player does.
   */
  const mirroringRef = useRef(false)
  const [watching, setWatching] = useState<
    { seat: number; gapped: boolean; diverged: boolean } | undefined
  >(undefined)
  useEffect(() => {
    // The same gate as the online-game watch, for the same reason (M11-01): spectating somebody
    // else's ball needs an online game, which needs an account. A browser that has never resolved
    // an entitlement has neither, and paid ~55 KB to find that out.
    if (knownTier() === undefined) return
    let alive = true
    let stop: (() => void) | undefined
    void import('../account')
      .then((mod) => {
        if (!alive) return
        stop = mod.watchTrace((view) => {
          const runtime = runtimeRef.current
          if (!alive || runtime === null) return
          // **Nothing to watch, or it is this player's own ball.** A machine showing a spectated
          // game while its owner is holding the flippers would be showing them somebody else's.
          if (view === undefined || myTurnRef.current || view.trace.events.length === 0) {
            runtime.stopWatching()
            mirroringRef.current = false
            setWatching(undefined)
            return
          }
          runtime.watchBall(view)
          mirroringRef.current = true
          // Read after `watchBall`, so the first packet of a new seat is judged against the game
          // it just started mirroring rather than the one it stopped.
          const diverged = (runtime.spectating?.divergedAt ?? -1) >= 0
          setWatching((held) =>
            held?.seat === view.seat && held.gapped === view.gapped && held.diverged === diverged
              ? held
              : { seat: view.seat, gapped: view.gapped, diverged },
          )
        })
      })
      .catch(() => undefined)
    return () => {
      alive = false
      stop?.()
      runtimeRef.current?.stopWatching()
    }
  }, [])

  /**
   * Hand the machine back the moment it is this player's turn again.
   *
   * A separate effect because the *stream* cannot do it: the player whose turn just ended stops
   * publishing, so no further packet arrives to notice with — and a machine left mirroring a
   * finished ball is one whose owner cannot plunge.
   */
  const myTurnRef = useRef(false)
  useEffect(() => {
    myTurnRef.current = online?.myTurn === true
    if (online?.myTurn !== true && online !== undefined) return
    if (mirroringRef.current) {
      runtimeRef.current?.stopWatching()
      mirroringRef.current = false
      setWatching(undefined)
    }
  }, [online])

  /**
   * Stream this ball to whoever is watching it (M8-18).
   *
   * **On a timer rather than on a render**, because the cadence belongs to the lane and not to the
   * frame rate: a phone that renders at 30 Hz and a headset that renders at 90 must put the same
   * packets on the wire, and a spectator's picture must not depend on how busy the player's GPU
   * is. The runtime is read through its ref for the same reason the pause button is — the machine
   * outlives any one render and this must not restart when the score changes.
   *
   * Only while it is this player's ball. `publishTrace` re-checks that against the canonical
   * document, so the two never disagree; this effect exists to stop a timer that has nothing to do
   * rather than to make the decision.
   */
  useEffect(() => {
    if (online === undefined || !online.myTurn) return
    let alive = true
    const handle = setInterval(() => {
      const local = runtimeRef.current?.localTrace
      if (local === undefined) return
      void import('../account')
        .then((mod) => {
          if (alive) mod.publishTrace(local)
        })
        .catch(() => undefined)
    }, TRACE_INTERVAL_MS)
    return () => {
      alive = false
      clearInterval(handle)
    }
  }, [online])

  const launch = useCallback(() => runtimeRef.current?.launch(), [])
  const pause = useCallback(() => runtimeRef.current?.togglePause(), [])
  /**
   * Give the machine the whole screen, or give the bars back (M3-45).
   *
   * **Both halves are asked for inside the click**, because `requestFullscreen` requires a user
   * gesture and one reached through an `await` on something else has usually lost it — the same
   * rule `EnterHeadset` follows for `requestSession`, and for the same reason.
   *
   * The signal is set first and unconditionally. Fullscreen may be refused; the rows are ours and
   * collapse either way, so a browser with no element fullscreen still gets the larger machine.
   */
  const toggleFocus = useCallback(() => {
    const next = !isPlayFocused()
    setPlayFocus(next)
    // **Written down as a choice, because it is one** (M3-45). The state is not what is stored —
    // see `writeFocusChoice` — so this records the press and never the outcome of the two async
    // calls below, either of which a browser may refuse without the player having changed a thing.
    writeFocusChoice(next ? 'on' : 'off')
    if (next) void requestFullscreen(stageRef.current ?? undefined)
    else void exitFullscreen()
  }, [])
  // Read through the ref like Pause and Focus are, so the HUD never holds a second copy of a
  // state the runtime owns — and so a mute made from the wrist menu redraws this button.
  const toggleSound = useCallback(() => runtimeRef.current?.toggleAudioMuted(), [])
  const muteMic = useCallback(() => runtimeRef.current?.toggleMicrophoneMute(), [])
  const muteVoice = useCallback(() => runtimeRef.current?.toggleVoiceMute(), [])
  const dismissMicNotice = useCallback(() => runtimeRef.current?.dismissMicrophoneNotice(), [])
  /**
   * Decline the table (M2-19a).
   *
   * Through the runtime like every other control here, never into the game: what this decides ends
   * up on a leaderboard row, and a row a replay cannot reproduce is a row the server refuses.
   */
  const skipInitials = useCallback(() => runtimeRef.current?.skipInitials(), [])
  const previousLetter = useCallback(() => runtimeRef.current?.tapFlipper('left'), [])
  const nextLetter = useCallback(() => runtimeRef.current?.tapFlipper('right'), [])

  /**
   * Accept the offer.
   *
   * **Not `await`ed before the permission call.** On iOS `requestPermission` must be reached from
   * inside the click that asked for it, and anything awaited first — a state update, a storage
   * write — spends the gesture and gets `NotAllowedError` for a player who was about to say yes.
   * So the request goes first and everything else happens once it answers.
   */
  const acceptMotion = useCallback(() => {
    void requestMotionPermission(motionHost).then((answer) => {
      if (answer === 'granted') {
        writeMotionChoice('on')
        setMotion('on')
        runtimeRef.current?.setMotionEnabled(true)
        return
      }
      if (answer === 'denied') {
        // The *browser* refused, not the player. Stored all the same — asking again on the next
        // ball would be a machine nagging for something the operating system has already
        // settled — but said out loud, because a button that was pressed and did nothing is
        // otherwise a broken button.
        writeMotionChoice('off')
        setMotion('off')
        setMotionRefused(true)
      }
      // `unasked` — the call never reached the player, so the offer stays where it was.
    })
  }, [motionHost])

  // The refusal is an event, so its line goes away. A permanent sentence about something the
  // browser decided once is a machine that looks like it is still failing at something.
  useEffect(() => {
    if (!motionRefused) return
    const timer = setTimeout(() => setMotionRefused(false), REFUSAL_MS)
    return () => clearTimeout(timer)
  }, [motionRefused])

  /**
   * And the confirmation goes away too.
   *
   * Starts only once the line is actually up — `motion === 'on'` and nothing felt yet — so the
   * clock measures how long the player has *seen* it rather than how long the machine has been
   * open. A shove clears it early through `motionFelt`; this is the floor under the case where no
   * shove ever comes.
   */
  useEffect(() => {
    if (motion !== 'on' || motionFelt || motionHintOver) return
    const timer = setTimeout(() => setMotionHintOver(true), MOTION_HINT_MS)
    return () => clearTimeout(timer)
  }, [motion, motionFelt, motionHintOver])

  /** Pick the interrupted game back up. Rebuilds the machine around its trace. */
  const takeResume = useCallback(() => {
    setResumeFrom(offered?.trace)
    setOffered(undefined)
  }, [offered])

  /** Abandon it. Cleared immediately, because starting a fresh game is what abandoning means. */
  const dropResume = useCallback(() => {
    setOffered(undefined)
    void historyRef.current?.clearInProgress(identity.machineId)
  }, [])

  /**
   * The confirmed End game, from the wrist menu or the stage menu (M5-24, M8-80).
   *
   * **It used to end nothing.** This effect cleared the saved resume record and wrote a notice, and
   * the ball played on: armed, confirmed, and the machine still reading "Ball 1 of 3, a ball is
   * waiting in the lane" three seconds later. `machineRuntime`'s own note had said all along what
   * ending is — *"a shell action: unmount the machine, keep or discard what it wrote"* — and the
   * shell did the discarding and never the rest.
   *
   * Four things, in the order a player experiences them:
   *
   *   * **The game goes**, by `abandonGame`, which restores the machine's pristine simulation on
   *     the same objects. No core call, no game over, no score, and the recorder is reset with it.
   *   * **The resume record goes**, both halves — a row that says it ended the game and then offers
   *     it back is a row that lied.
   *   * **The screen comes back.** Focus mode hides the top bar *and* the bottom navigation, which
   *     is every way out; ending the game and staying inside it would leave a player on a
   *     chrome-less screen with nothing left to do on it, which is the trap this product has paid
   *     for once already. Focus mode means "I am playing now" and they have just said they are not.
   *     Fullscreen goes with it, as the same pair does everywhere else in this file.
   *
   *     **The player's standing choice is deliberately not touched.** `setPlayFocus` is an
   *     in-memory signal and the stored answer belongs to `firstRun`, so somebody who likes focus
   *     mode gets it again on their next pull of the plunger. Ending a game is a statement about
   *     this game, not about how they like to play.
   *   * **It says so**, because a destructive act that is silent is indistinguishable from one that
   *     did nothing — which is exactly what this was.
   *
   * **Where it leaves them is on the machine, in attract, and nowhere else.** The menu already has
   * "Another machine" for leaving; a destructive row that also navigated would collapse two
   * different intentions into one and take away the commonest reason to press this — ending a bad
   * game and starting a fresh one at once, which from attract is one pull of the plunger.
   */
  useEffect(() => {
    if (!endRequested) return
    setEndRequested(false)
    runtimeRef.current?.abandonGame()
    dropResume()
    void historyRef.current?.clearInProgress(identity.machineId)
    setPlayFocus(false)
    void exitFullscreen()
    setShellNotice(t('stage.game-ended'))
  }, [endRequested, dropResume])

  /**
   * And it goes away again.
   *
   * Six seconds: long enough to read a sentence somebody was not expecting, short enough that it is
   * gone before the next ball. Its own effect rather than a timeout inside the one above, so the
   * clock restarts if the notice is written again and there is exactly one timer to cancel.
   */
  useEffect(() => {
    if (shellNotice === '') return
    const timer = setTimeout(() => setShellNotice(''), 6000)
    return () => clearTimeout(timer)
  }, [shellNotice])

  const declineMotion = useCallback(() => {
    writeMotionChoice('off')
    setMotion('off')
    runtimeRef.current?.setMotionEnabled(false)
  }, [])

  // Recomputed on every readout, which is at most once a frame and usually far less: `publish`
  // already refuses to report a readout that has not changed.
  const status: MachineStatus = {
    phase: readout.phase,
    ball: readout.ball,
    ballsPerGame: readout.ballsPerGame,
    player: readout.player,
    players: readout.players,
    score: readout.score,
    tilted: readout.tilted,
    awaitingLaunch: readout.awaitingLaunch,
    initialsAt: readout.initialsAt,
    initials: readout.initials,
  }
  useEffect(() => {
    const line = narrate(status, saidFor.current)
    saidFor.current = status
    // Only replaced when there is something new to say. Re-rendering the same sentence into a
    // live region makes a screen reader read it again, which is how a polite region becomes an
    // impolite one.
    if (line !== undefined) setSpoken(line)
    // `status` is rebuilt every render; the readout it comes from is what actually changes.
  }, [readout]) // eslint-disable-line react-hooks/exhaustive-deps

  // M8-10. Read from the readout rather than held as its own state, so there is exactly one
  // answer and it is the runtime's — a second copy here would be a second thing to keep in step
  // with a control the player can also reach from a wrist panel.
  /**
   * The four steps of the getting-started strip (G2-11).
   *
   * Held here rather than read per render, because marking a step writes to storage and the strip
   * has to redraw when one lands — a value read straight out of `localStorage` on every render
   * would be correct and would never cause the redraw that shows it.
   */
  const noteStep = useCallback((step: Parameters<typeof markOnboarding>[0]) => {
    // Idempotent in the module, so this is safe to call from a per-frame effect: a player makes a
    // ramp many times a ball and only the first one writes.
    //
    // **No React state any more** (M8-77). It was held here because the strip was drawn here and a
    // value read out of `localStorage` each render would be correct without ever causing the
    // redraw that shows it. The strip is on the settings page now, which reads the store when it
    // mounts, so this page marks and draws nothing — and a `useState` nothing renders is a render
    // per ramp for no reason.
    markOnboarding(step)
  }, [])

  /**
   * The spoken report, built at the moment it is asked for (G2-09).
   *
   * A callback rather than a value, and that is the whole shape of it: the snapshot walks every
   * lamp on the playfield, and a value would do that on every frame for a report nobody is
   * listening to. Empty with no runtime, which is the frame before the machine is built.
   */
  const aloud = useCallback((): AloudSnapshot | undefined => runtimeRef.current?.aloud(), [])

  /**
   * Watch the four things the strip is about (G2-11).
   *
   * **Read off the readout and the runtime rather than from four new callbacks**, because all four
   * are states rather than events and the readout already changes when any of them does: a ball has
   * been played once the game has moved past ball one *or* ended; a ramp has been made while a ball
   * is off the playfield; a mode has started while one is running. Only the fourth is genuinely an
   * event, and it is marked where the score is actually saved.
   */
  useEffect(() => {
    const runtime = runtimeRef.current
    if (runtime === null) return
    if (readout.ball > 1 || readout.phase === 'gameOver') noteStep('playBall')
    if (runtime.ballOffPlayfield) noteStep('makeRamp')
    if (runtime.activeModes.length > 0) noteStep('startMode')
  }, [readout, noteStep])

  // M8-10. Read from the readout rather than held as its own state, so there is exactly one
  const micBlocked = shouldTellAboutMicrophone({
    mute: { microphone: readout.micMuted, others: readout.voiceMuted },
    access: readout.micAccess,
    noticed: readout.micNoticed,
  })
  // M8-17. Whether the machine is this player's to plunge right now. **`undefined` is the third
  // answer and it is the common one**: no online game, so no online opinion — which is not the
  // same as "not your turn" and must not disable anything.
  const myBall =
    online === undefined || online.game.phase !== 'playing' ? undefined : online.myTurn
  const upNext = online?.upNext

  const waiting = readout.phase === 'attract' || readout.awaitingLaunch
  // Held level with the table while a game is in progress, and frozen the moment one ends.
  useEffect(() => {
    if (readout.phase === 'gameOver') return
    bestBefore.current = table[0]?.score ?? 0
  }, [table, readout.phase])

  // 'gameOver' is the phase *after* initials entry has finished with every player who qualified;
  // during entry the phase is 'highScoreEntry' and the machine is asking for something else.
  const finished = readout.phase === 'gameOver'
  // Strictly greater, and only for a solo game. Equalling a previous best is not beating it, and
  // in a hot-seat game "your best" belongs to four people rather than to this browser.
  const personalBest =
    finished && readout.players === 1 && readout.score > 0 && readout.score > bestBefore.current
  /**
   * How many games have ended since this stage was mounted (M10-30).
   *
   * **The second thing that retires the Builder aside, and the one that covers everybody who never
   * takes it.** `built` retires it for a player who does — which is what M10-25's own door leans on
   * — and nothing retired it for a player who does not, on a panel that arrives every few minutes.
   *
   * So the aside waits. The first game over is the one moment "Keep this score" will ever have: a
   * visitor has just earned a score that vanishes when the tab closes, and a second destination
   * underneath the offer about *that* competes with the strongest thing on the panel.
   * By the second, the player has decided they like this, which is when a machine of their own
   * becomes a thing to want rather than a thing being sold.
   *
   * Counted rather than timed, because what is being waited for is a game played and not a minute
   * passed. `attract` is only the phase a stage *starts* in, so nothing here counts the front door
   * demonstrating itself — `gameOver` is reached by playing.
   *
   * **It counts this mount, and the question is about this browser** (M10-31). A reload starts it
   * at zero, so a player who loads the page for each game never reaches two and never sees the
   * aside — reported from a browser whose local table held four scores. That is why it is no longer
   * the only signal: `playedBefore` below answers the same question in a way a reload cannot erase,
   * and this one stays for the case that one cannot see.
   */
  const [gamesEnded, setGamesEnded] = useState(0)
  useEffect(() => {
    if (finished) setGamesEnded((count) => count + 1)
  }, [finished])
  /**
   * Whether the account door is a real destination yet, or a state to say out loud.
   *
   * It asks about `/account` rather than `/leaderboards`, which is what it used to ask about and
   * is why the offer below was dead. "Accounts arrive with the boards" was true when it was
   * written; the boards arrived, `unavailableReason('/leaderboards')` started answering
   * `undefined`, and nothing was watching — so the end of every game showed a **permanently
   * disabled** button captioned with a fallback that says accounts are still coming. The one
   * moment in the funnel where a player has just earned something was offering them a control
   * that could not be pressed.
   */
  const accountUnavailable = unavailableReason('/account')
  /**
   * What the end of a game offers, and to whom (08 §1, 01 §6, M10-16).
   *
   * **Two doors, and telling them apart is the whole point.** `gateVerdict` answers
   * `needs-account` for a visitor short of something a *free* account gives and `needs-pro` only
   * for what is actually bought — so a visitor is offered an account and never a price, because a
   * price for something that costs nothing is a lie, and a player who already has an account is
   * never offered one back. That second half is `Paywall`'s own rule: showing somebody a feature
   * they have had since they signed up is the fastest way to make an offer read as dishonest.
   *
   * **This is the moment of desire, not an interstitial** (M10-16). Nothing here interrupts: the
   * game ended on its own, the score is already stated above, and the offer is one button beside
   * "Play again" that names what the player just did — kept it, or found the rest of the arcade.
   *
   * The tier is read **once, at mount**, like `touchPrimary` and for the same reason: this
   * component re-renders on every frame a score moves, and `knownTier` reads `localStorage`. It
   * cannot go stale in a way that matters — signing in happens on `/account`, and coming back here
   * is a fresh mount.
   */
  const endOffer = endOfGameOffer(tier, built)
  /**
   * Whether the panel also says, quietly, that the Builder is there (M10-30).
   *
   * **Not an upsell, which is why it is allowed to exist at all.** `builder.open-title` has said
   * since M10-25 that designing and test-playing are free, and the route gates nothing: what Pro
   * buys is a machine *leaving this browser*. So this is a destination a visitor can walk through
   * today, and the moment it routes to `/account?want=builder-private` it has become an
   * advertisement for something the reader already has.
   *
   * **And not a third button.** Measured rather than assumed: `.stage__over-actions` wraps at
   * *two* already — 104 px of row for a 52 px button, at the panel's own `max-width: 34ch`, on a
   * 1280 px window as much as on a 360 px phone — so a third peer would have made three stacked
   * blocks with the least important of them reading as equal in weight to "Play again". The panel
   * already owns the shape for a control that is real but not the point — `button--quiet`, the
   * initials row's "Skip" — and this is a link because it is navigation, so a middle click does
   * what a middle click should.
   */
  /**
   * Had this browser finished a game on this machine before the one just played (M10-31)?
   *
   * **The same question `gamesEnded` asks, answered from disk instead of from memory.** `bestBefore`
   * is frozen the moment a game ends — that is what makes it "before" for `personalBest` — and it is
   * seeded from the local table during attract, which is read from IndexedDB at mount. So on a fresh
   * page load carrying history it is already the top score from a previous visit, which is exactly
   * "you have seen this panel here before".
   *
   * Per machine, because the local table is: a player with five NEON NOVA scores meeting IRON
   * FATHOM for the first time waits one game. That is the rule working rather than a gap in it —
   * the panel they are looking at is this machine's, and its table is empty.
   *
   * Kept beside the counter rather than replacing it, because a first game that scored nothing
   * writes no row: then this stays `false` for ever and `gamesEnded` is the only thing that can
   * answer.
   */
  const playedBefore = bestBefore.current > 0
  const buildAside = builderAsideOffered(tier, built, gamesEnded, playedBefore)
  const entering = readout.initialsAt >= 0
  const scoredGameInProgress = readout.phase !== 'attract' && readout.phase !== 'gameOver' && !entering
  // The lane, not the attract screen and not mid-ball. See the header.
  const inTheLane = readout.awaitingLaunch && readout.phase !== 'attract'
  /**
   * Has the cabinet's own plunger taken the job from the overlay's button (M3-49, M3-51)?
   *
   * **It stands down on a touch device now, not merely once a ball is in the lane.** The narrower
   * rule left the one moment that matters most — a stranger arriving, machine in attract — showing
   * a button laid across the playfield *and* an inert plunger, so the button was not redundant, it
   * was in the way: the thing it told you to do was the one thing you could not do. M3-51 makes the
   * column live in attract as well, and `plungerAvailable` in `machineRuntime` is now exactly the
   * condition `waiting` is, so wherever this button would have appeared the plunger can do its job.
   *
   * **Kept on anything a finger does not drive.** The touch listener binds `touchstart`, which a
   * mouse never fires, so on a desktop this is the only plunger there is — and the only launch
   * control a screen reader can reach, because the band is `aria-hidden`.
   *
   * **`myBall === false` keeps it too**, because in that state it is not a control at all: it is
   * the sentence explaining whose ball it is, and hiding it would leave a phone player waiting with
   * no idea what for.
   */
  const plungerOnCabinet = touchPrimary && myBall !== false
  const askAboutMotion = offerable && motion === undefined && inTheLane
  /**
   * Turned on, and the sensor has not yet produced anything.
   *
   * **Cleared by either a shove or a clock, and it needs both.** The shove is the honest signal —
   * the moment the sensor proves itself the line has nothing left to say. But a granted permission
   * that emits nothing is a real state on real hardware (a desktop, an emulated device, a phone
   * whose `devicemotion` never fires), and waiting on a shove alone left the banner up forever:
   * it owns the top strip, and through `askIsUp` it suppresses the key guide for the whole
   * session. So `motionHintOver` puts a floor under the case where the proof never comes.
   */
  const proveMotion =
    offerable && motion === 'on' && !motionFelt && !motionHintOver && inTheLane
  /**
   * Is a banner asking the player something? (M3-56.)
   *
   * **The key guide yields to every one of them, and that was measured rather than assumed.**
   * `.stage__ask` is pinned at `top: 68px` with `left` and `right` at 12 px, so an ask is a
   * full-width banner whose height is a function of its own sentence — it owns the top strip
   * outright while it is up. On a 1347 × 823 window a bottom-anchored rail clears it with 56 px to
   * spare; on 1100 × 620 the same rail overlapped it by 35,194 px² and the top of the collapse
   * control was no longer clickable. **No anchor solves that**, because both are variable and the
   * pane between them is not always big enough for either to give way.
   *
   * So the rail is not drawn while one is up, and comes back in whatever state the player left it.
   * The cost is honest and worth naming: `stage__ask--online` stays up for a whole online game, so
   * the guide is unavailable there. A legend arguing with a banner over the same pixels is worse
   * than a legend that waits, and the moment to learn the keyboard is a solo first game rather
   * than a lobby.
   */
  /**
   * Is the interrupted game on screen, waiting to be answered (M3-39)?
   *
   * **Named once because three things now depend on it**, and the last of them is the reason it
   * stopped being an inline condition: 08 §1's primary control used to render underneath this
   * card, and "PULL THE PLUNGER" over an unanswered question is both of the things the ask rules
   * already forbid. It is *two things asking for a press in the same place* — the `.stage__ask`
   * header's own words — and pressing it answered the question the wrong way round: `launch`
   * starts a fresh game, the phase leaves `attract`, and the card vanishes, so an interrupted
   * game was discarded by a control that never mentioned it.
   *
   * It also made the card the taller half of a column that does not fit. In landscape the pane is
   * about 205 px; the card and the button together are 192 px of content inside 48 px of overlay
   * padding, so something had to be clipped and the bottom-aligned column clipped the sentence.
   * With the button gone the card is 128 px and has 53 px to spare — the layout follows the rule
   * rather than being tuned around its absence.
   */
  const resumeOffered = offered !== undefined && readout.phase === 'attract'
  const askIsUp = askAboutMotion || proveMotion || online !== undefined || resumeOffered

  return (
    <section
      className={`stage stage--${orientation}`}
      ref={(node) => {
        stageRef.current = node
        setStage(node)
      }}
      aria-label={machineTitle}
      // **Focusable on purpose, and out of the tab order on purpose** (M3-56). `Space` and `Enter`
      // activate a focused `button` element and both launch the ball, so the key guide's collapse
      // control hands focus back here rather than keeping it — otherwise a player who hid the panel
      // would find their plunger toggling it instead. `-1` is what makes that legal without adding
      // the machine itself to the tab order, where it would be a stop with nothing to press.
      tabIndex={-1}
      // The rows come from `phone.ts`, not from the stylesheet, so that the band a finger is
      // tested against and the band that is drawn are the same numbers. Landscape collapses the
      // stack: no DMD strip, and the zones become the left and right thirds (08 §5.3).
      style={{ gridTemplateRows: orientation === 'portrait' ? stageRows(box.height) : '0fr 1fr 0fr' }}
    >
      {/*
        The page’s heading (S3-04), for a screen reader and for a crawler and for nobody else.
        `sr-only` because 08 §1 gives the front door no copy over the machine — and a document
        whose only top-level heading was the navigation bar’s tab label had no subject at all.
      */}
      <h1 className="sr-only">{heading ?? machineTitle}</h1>
      {galleryOpen ? <Suspense fallback={<p className="notice" role="status">Preparing the arcade…</p>}><MachineGalleryDialog current={identity.machineId}
        onChoose={(id) => { void navigate('/play/' + encodeURIComponent(id)) }}
        onClose={() => {
          setGalleryOpen(false)
          if (galleryResume.current) runtimeRef.current?.setPaused(false)
          galleryResume.current = false
        }} /></Suspense> : null}

      {/* The backbox, reduced to what matters (08 §5.1). Hidden in landscape, where the real
          backglass is in the scene and a second copy of the same dots would be two DMDs. */}
      <canvas className="stage__dmd" ref={dmdRef} aria-hidden="true" />

      <div className="stage__pane">
        <canvas className="stage__canvas" ref={canvasRef} />

        {/* What the keyboard does (M3-56, 08 §4.3). A phone never sees it — its primary pointer
            is coarse — and neither does a window too tall to hold a rail beside a cabinet without
            taking the width from the playfield. */}
        {shouldShowKeyGuide({ pointerIsFine, orientation }) && !askIsUp ? (
          <KeyGuide
            playSurface={stage}
            liftAboveTheCabinet={keyGuideLift(layout, orientation)}
            headroom={keyGuideHeadroom(box.height, hudBox.height, keyGuideLift(layout, orientation))}
          />
        ) : null}

        {/* Until the runtime has a frame on the glass, the skeleton stays: a blank black
            rectangle for a second and a half is indistinguishable from a page that has failed. */}
        {running ? null : (
          <div className="skeleton" aria-hidden="true">
            <div className="skeleton__cabinet" />
            <p className="skeleton__label">{t('stage.building')}</p>
          </div>
        )}

        {/* NFR-025's navigable surface. Off-screen rather than hidden: `display: none` and
            `visibility: hidden` both take an element out of the accessibility tree, so a status
            list nobody can see would also be a status list nobody can read. */}
        <section className="sr-only" aria-label={t('stage.status')}>
          <dl>
            {statusFields(status).map((field) => (
              <div key={field.label}>
                <dt>{field.label}</dt>
                <dd>{field.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* …and the handful of things that are genuinely events. `polite`, so it waits for a gap
            rather than interrupting a callout that is already being read. */}
        <p className="sr-only" role="status" aria-live="polite">
          {spoken}
        </p>

        {AccountUtility!==null&&(
          <dialog className="stage__account-utility" aria-label="Account — return to your table"
            ref={node=>{if(node&&!node.open){if(typeof node.showModal==='function')node.showModal();else node.setAttribute('open','')}}}
            onClose={()=>setAccountUtility(null)}
            onClickCapture={event=>{
              const link=(event.target as Element).closest?.('a[href]')
              if(link&&!link.hasAttribute('download')){
                event.preventDefault()
                globalThis.open((link as HTMLAnchorElement).href,'_blank','noopener,noreferrer')
              }
            }}>
            <h2>Account</h2>
            <p>Your table and draft are kept. Account links open separately. After returning, enter the headset again to continue. Play stays paused.</p>
            <button type="button" title="Return to your table with the draft kept"
              onClick={event=>event.currentTarget.closest('dialog')?.close()}>Return to your table</button>
            <AccountUtility utility />
          </dialog>
        )}
        <div className="stage__hud" ref={setHud}>
          <div>
            {/* **The score belongs to the backglass when there is one (08 §4.2).** The machine's
                own DMD is the primary readout, and this HUD sits directly over it — so printing
                the score here too drew the same number twice, overlapping, which is exactly the
                duplication a player sees. On a machine with no display of its own this is the
                only score there is, so it comes back rather than being deleted.

                `aria-live="off"` on purpose. A score that changes forty times a second would make
                a screen reader talk continuously and drown out everything that matters. The score
                is navigable — the `sr-only` status list above carries it for a reader either way,
                so nothing is lost when this is absent. */}
            {readout.hasDisplay ? null : (
              <p className="stage__score" aria-live="off">
                {readout.score.toLocaleString()}
              </p>
            )}
            {/* Once the last ball is gone, "Ball 3 of 3 · save 5.8s · TILT" is a sentence about
                something that is no longer happening, frozen on screen for as long as the
                machine waits — which is most of what made the finished machine read as broken. */}
            <p className="stage__meta">
              {finished || entering ? (
                t('stage.game-over')
              ) : (
                <>
                  {/* The ball number is on the backglass too. The ball *save* and the tilt are
                      not — they are the countdown and the state a screen has to say out loud,
                      per 08 §4.2's "what a screen needs and a cabinet does not" — so those stay
                      whatever the machine displays. `.filter(Boolean).join(' · ')` rather than a
                      chain of prefixed strings, because with the first item now optional a
                      hard-coded separator leaves a leading " · " on a machine with a DMD. */}
                  {[
                    readout.hasDisplay ? '' : `Ball ${readout.ball} of ${readout.ballsPerGame}`,
                    readout.players > 1 ? `Player ${readout.player} of ${readout.players}` : '',
                    readout.ballSaveSeconds > 0 ? `save ${readout.ballSaveSeconds.toFixed(1)}s` : '',
                    readout.tilted ? 'TILT' : '',
                  ]
                    .filter((part) => part !== '')
                    .join(' · ')}
                </>
              )}
            </p>
          </div>
          <div className="stage__hudControls">
            {/* 06 §4's "a mute control in the wrist menu and in the HUD". Shown only once a room
                has actually been joined: a mute button with no microphone behind it does nothing
                when it is pressed, which is the one thing a mute button must never do. */}
            {/*
              Mute the machine (07 §7). **First in the cluster and always present**, unlike the two
              beside it: the voice controls come and go with the arcade, and a button whose position
              depends on whether a room has been joined is a button that gets mis-pressed by a thumb
              already reaching for it. This one is the only control here that is wanted in a hurry.
            */}
            <SoundControl muted={readout.audioMuted} onToggle={toggleSound} />
            {/*
              "Read machine aloud" (06 §6, G2-09). **Free on every tier and always present**, like
              the mute beside it: it is an accessibility commitment rather than a feature, and a
              control that came and went would be one a player could not learn the position of.
            */}
            <ReadAloud report={aloud} />
            {inArcade ? (
              <VoiceControls
                micMuted={readout.micMuted}
                micAccess={readout.micAccess}
                voiceMuted={readout.voiceMuted}
                onToggleMic={muteMic}
                onToggleVoice={muteVoice}
              />
            ) : null}
            {scoredGameInProgress ? <button
              type="button"
              className="button button--quiet stage__pause"
              title={readout.paused ? t('stage.resume-machine-title') : t('stage.pause-title')}
              aria-pressed={readout.paused}
              onClick={pause}
            >
              <PausePlayGlyph paused={readout.paused} />
              <span className="sr-only">
                {readout.paused ? t('stage.resume-machine') : t('stage.pause')}
              </span>
            </button> : null}
            {/*
              The flat screen's wrist menu (M3-55).

              **Focus mode moved into it, and Pause did not.** M3-45 put focus beside Pause because
              "a control you have to leave the game to reach is one nobody uses twice" — and that
              argument was about the *settings page*, two navigations away, not about a panel that
              opens under the thumb already on this corner. Pause stays out because a ball is in
              play; the sound and the microphone stay out for the reason `wristMenu.ts` gives about
              mute controls and submenus.

              **It is here that `endGame` and `otherMachine` finally become reachable.** Both were
              implemented below and called only from `onXrMenu`, so until now a player without a
              headset could not end a game or change machine once one had started.
            */}
            <StageMenu
              rows={stageMenuRows({
                t,
                gameInProgress: scoredGameInProgress,
                paused: readout.paused,
                focused,
                onPause: pause,
                onToggleFocus: toggleFocus,
                onEndGame: () => setEndRequested(true),
                onAnotherMachine: () => {
                  if (browseMachinesRef.current !== undefined) browseMachinesRef.current()
                  else void navigate('/arcade')
                },
                onSettings: () => void navigate('/settings'),
                // 11 §6's report control, on the surface a player is standing on when they want
                // it. Offered only for a machine somebody wrote — see `isReportable`.
                ...(reportable ? { onReport: () => setReporting(true) } : {}),
              })}
            />
          </div>
          {/*
            **A line of its own under the HUD's two columns** (M9-32). Both belong to the chrome
            rather than to the glass — `touch-affordance.test.ts` asks that nothing sits on the
            playfield that is not about the playfield — and both are full-width because the columns
            beside them are not: squeezed into the left one, "Playing: NEON NOVA — your machine"
            wrapped to three lines and pushed its own button down over the backglass. The HUD is
            measured, so the key guide already knows to move down for whatever these take.

            The unscored line goes **where the score would be**, because it is a fact about the
            score: a player who switched to their own machine has to be told once that this game
            goes nowhere, and not only in a Builder they may not have come from.
          */}
          {/*
            The report panel, drawn beside the machine rather than over it (11 §6). A modal over a
            live playfield is a modal a ball drains behind, and the flipper keys stay live
            underneath every overlay on this screen — `StageMenu`'s own header says so.
          */}
          {reporting ? (
            <ReportMachine
              machineTitle={machineTitle}
              onClose={() => setReporting(false)}
              onSend={async (reason, detail) =>
                (await import('../account')).reportMachine({
                  machineId: identity.machineId,
                  semver: identity.machineVersion,
                  reason,
                  detail,
                })
              }
            />
          ) : null}
          {unscored ? (
            <p className="stage__unscored" title={t('stage.unscored-title')}>
              {t('stage.unscored')}
            </p>
          ) : null}
          {/* Which machine this is, and the one control that changes it. Absent unless the host
              supplied it — which is every route but the front door, and every browser with no
              draft. See `switcher`. */}
          {switcher}
        </div>

        {/* 08 §5.2's offer, in the one gap in a game where nothing else is being asked. Not a
            modal: a dialogue over a machine is a machine you have to dismiss before you can
            play, and the answer to this one is genuinely optional. */}
        {askAboutMotion ? (
          <div className="stage__ask" role="group" aria-label={t('stage.motion-offer')}>
            <p className="stage__ask-text">
              Nudge by moving your phone.
              {systemPrompt ? ' Your browser will ask for permission.' : ''}
            </p>
            <div className="stage__ask-actions">
              <button
                type="button"
                className="button button--primary"
                title={t('stage.motion-on-title')}
                onClick={acceptMotion}
              >
                {t('stage.turn-on')}
              </button>
              <button
                type="button"
                className="button button--quiet"
                title={t('stage.motion-off-title')}
                onClick={declineMotion}
              >
                {t('stage.no-thanks')}
              </button>
            </div>
          </div>
        ) : null}

        {proveMotion ? (
          <div className="stage__ask stage__ask--hint">
            <p className="stage__ask-text">{t('stage.motion-enabled')}</p>
          </div>
        ) : null}

        <div className="stage__overlay">
          {/*
            The headset entry (08 §7), and the two things it has to say.

            **These were siblings of the canvas rather than children of this overlay, and the
            canvas is `height: 100%`** — so they flowed underneath it, past the bottom of a pane
            that clips its overflow. "Enter VR" and "Enter MR" were in the document, focusable by
            keyboard, and off-screen for everybody: a player could tab to a control nobody could
            see and be put into an immersive session by pressing return. Measured on a desktop
            Chrome with the emulated device installed — the pane ended at 861 px and the controls
            began at 861 px.

            First in the overlay, so they sit above the caption and the resume card: the overlay
            stacks from the bottom, and the entry control is the one thing here that is about
            leaving this screen rather than about the game on it.
          */}
          {/*
            …and it is now only here on a device that can actually enter one (M5-01a). On a phone
            the pane is short, so "pinned to the bottom of the overlay" put two buttons across the
            lower playfield — over the flippers, in the screenshot that started this — and, because
            the overlay's children take pointer events, they swallowed flicks aimed at the machine.
            On a device that cannot present, both buttons only navigate to `/headset` for a QR
            code, which is not worth covering the machine for. That handoff is on Settings.
          */}
          <EnterHeadset
            runtime={runtimeRef.current}
            presenting={presentingXr}
            onProblem={setXrProblem}
            onlyWhenPresentable
          />
          {xrProblem !== null ? (
            <p className="stage__notice" role="status">
              {xrProblem}
            </p>
          ) : null}
          {xrToast !== '' ? (
            <p className="stage__notice" role="status">
              {xrToast}
            </p>
          ) : null}
          {shellNotice !== '' ? (
            <p className="stage__notice" role="status">
              {shellNotice}
            </p>
          ) : null}

          {/* NFR-025: every spoken line is captioned. `polite`, so it waits for a gap rather than
              interrupting whatever the player was already being told. */}
          {readout.caption === '' ? null : (
            <p className="stage__caption" aria-live="polite">
              {readout.caption}
            </p>
          )}

          {readout.notice === '' ? null : (
            <p className="stage__notice" role="status">
              {readout.notice}
            </p>
          )}

          {/* A refused microphone, said once and never again unless it changes (M8-10, 06 §4:
              "non-blocking on a denied microphone").

              **A chip in the overlay rather than a dialogue**, and the distinction is the whole
              requirement: a player who declined a microphone declined it on purpose, and putting
              a box in front of the machine to discuss it would be arguing with them. It sits in
              the same stack as every other notice, is dismissible, and stays dismissed — until
              the microphone is *granted*, which clears it, so a later denial is announced again.

              The sentence names what still works. The call is created with a receive-only
              transceiver, so a player with no microphone still hears the whole room; leading with
              the failure would make a one-way room sound like a broken one. */}
          {micBlocked ? (
            <div className="stage__chip" role="status">
              <span className="stage__chip-text">{MIC_BLOCKED_NOTICE}</span>
              <button
                type="button"
                className="button button--quiet stage__chip-close"
                title={t('stage.dismiss-title')}
                onClick={dismissMicNotice}
              >
                <span aria-hidden="true">×</span>
                <span className="sr-only">{t('stage.dismiss')}</span>
              </button>
            </div>
          ) : null}

          {/* Graceful decline, said out loud (08 §5.2). The flick is not a consolation prize —
              it is the same nudge through the same queue, which is why the sentence names it
              rather than apologising. */}
          {motionRefused ? (
            <p className="stage__notice" role="status">
              {t('stage.motion-refused')}
            </p>
          ) : null}

          {renderError === null ? null : (
            <p className="stage__notice" role="alert">
              The 3-D machine could not start on this browser — {renderError}
            </p>
          )}

          {/* Who is playing (M8-17, FR-013).

              **Only in attract, and that is the rule rather than a limitation.** A game's player
              count is fixed when it starts — the core builds one player state per seat — so
              changing it mid-game would mean abandoning the game to answer a question nobody
              asked. A physical machine works the same way: you press start once more before the
              first plunge, and after that the backglass has as many reels as it has.

              Hidden entirely once an online game exists, because then the seats are the room's and
              not this browser's, and two ways to decide how many people are playing is two answers
              that can disagree. */}
          {readout.phase === 'attract' && running && online === undefined ? (
            <div className="stage__seats" role="group" aria-label={t('stage.players-title')}>
              <span className="stage__seats-label">{t('stage.players')}</span>
              <div className="segmented">
                {SEATS.map((count) => (
                  <button
                    key={count}
                    type="button"
                    className={
                      count === players ? 'segmented__option segmented__option--on' : 'segmented__option'
                    }
                    aria-pressed={count === players}
                    title={
                      count === 1
                        ? t('stage.play-alone')
                        : t('stage.take-turns', { others: count - 1 })
                    }
                    onClick={() => setPlayers(count)}
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {/* The other reels (M8-17). One component, two games: a hot-seat game reads its rows
              from the local session's standings and an online one from the canonical document's
              seats. A solo game draws none — one row saying what the big number above it already
              says is furniture. */}
          <Scoreboard
            rows={online === undefined ? standingsRows(readout) : seatRows(online.game)}
            caption={
              online === undefined
                ? readout.phase === 'attract'
                  ? undefined
                  : t('stage.ball-of', { ball: readout.ball, total: readout.ballsPerGame })
                : online.game.phase === 'playing'
                  ? t('stage.ball-of', { ball: online.game.ball, total: online.game.ballsPerGame })
                  : undefined
            }
          />

          {/* Playing together (M8-14 … M8-17). The control that was missing: three milestones
              built the seating model, the command channel and the resync, and none of them had a
              way in. Offered only in attract for the same reason the seat chooser is — a game
              opens before the first plunge, not during somebody's ball. */}
          {readout.phase === 'attract' && running && online === undefined ? (
            <button
              type="button"
              className="button button--quiet stage__together"
              title={t('stage.open-game-title')}
              disabled={joining}
              onClick={hostOnline}
            >
              {joining ? t('stage.opening-game') : t('stage.play-together')}
            </button>
          ) : null}

          {online === undefined ? null : (
            <div className="stage__ask stage__ask--online" role="group" aria-label={t('stage.online-game')}>
              <p className="stage__ask-text">
                {online.game.phase === 'lobby'
                  ? `${online.game.seats.length} of 4 seated. Others can join until the first ball is over.`
                  : online.game.phase === 'playing'
                    ? myBall === true
                      ? t('stage.your-ball')
                      : watchingLine(upNext, watching, t)
                    : online.game.phase === 'complete'
                      ? t('stage.game-over-note')
                      : t('stage.game-called-off')}
              </p>
              <div className="stage__ask-actions">
                {online.game.phase === 'lobby' && online.isHost ? (
                  <button
                    type="button"
                    className="button button--primary"
                    title={t('stage.start-title')}
                    onClick={startOnline}
                  >
                    {t('stage.start')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="button button--quiet"
                  title={t('stage.leave-title')}
                  onClick={leaveOnline}
                >
                  {t('stage.leave')}
                </button>
              </div>
            </div>
          )}

          {/* The interrupted game (M3-39). Only in attract, where there is nothing to interrupt:
              offering it over a ball in play would be asking somebody to abandon the game they
              are in the middle of, which is the opposite of what it is for. */}
          {resumeOffered ? (
            <div className="stage__ask stage__ask--resume" role="group" aria-label={t('stage.resume-heading')}>
              <p className="stage__ask-text">
                {offered.ball === BALL_UNKNOWN
                  ? t('stage.left-game', { score: offered.trace.claimedScore.toLocaleString(locale) })
                  : t('stage.left-game-ball', {
                      ball: offered.ball,
                      score: offered.trace.claimedScore.toLocaleString(locale),
                    })}
              </p>
              <div className="stage__ask-actions">
                <button
                  type="button"
                  className="button button--primary"
                  title={t('stage.resume-title')}
                  onClick={takeResume}
                >
                  {t('stage.resume')}
                </button>
                <button
                  type="button"
                  className="button button--quiet"
                  title={t('stage.start-fresh-title')}
                  onClick={dropResume}
                >
                  {t('stage.start-fresh')}
                </button>
              </div>
            </div>
          ) : null}

          {/* High-score entry. The machine is asking, so the machine has to look like it is
              asking. The buttons drive the same two flipper controls a cabinet uses — they are
              not a second way in, they are the same way in with something visible on it. */}
          {entering ? (
            <div className="stage__over" role="group" aria-label={t('stage.initials-title')}>
              <p className="stage__over-line">
                {t('stage.makes-table', { score: readout.score.toLocaleString(locale) })}
              </p>
              <p className="stage__initials">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className={i === readout.initialsAt ? 'stage__letter stage__letter--at' : 'stage__letter'}
                  >
                    {readout.initials[i] === ' ' ? '␣' : readout.initials[i]}
                  </span>
                ))}
              </p>
              <div className="stage__over-actions">
                <button
                  type="button"
                  className="button"
                  title={t('stage.previous-letter')}
                  onClick={previousLetter}
                >
                  ◀
                </button>
                <button
                  type="button"
                  className="button button--primary"
                  title={t('stage.accept-letter')}
                  onClick={launch}
                >
                  {t('stage.ok')}
                </button>
                <button type="button" className="button" title={t('stage.next-letter')} onClick={nextLetter}>
                  ▶
                </button>
                {/*
                  The way out (M2-19a).

                  **In this row, and it was measured into it rather than chosen.** It began on its
                  own line below, on the argument that ◀ OK ▶ are one control in three parts and a
                  fourth button in the same row is reachable by the same rhythm. That argument is
                  still true and it lost to a bigger one: on an iPhone in landscape the pane is
                  about 205 px, and a fourth row put OK itself below the fold. A control you have
                  to scroll to find is worse than a control that is close to another one.

                  What is left of the original worry is paid for two ways — the separation in
                  `.stage__over-skip`, and `--quiet`, because the machine is asking for initials
                  and OK is the answer. A pinball machine that pushed you towards *not* signing the
                  table would be arguing against itself. `flex-wrap` on the row means a narrow
                  portrait phone still drops it to its own line, which is where it always wanted
                  to be and where there is room for it.
                */}
                <button
                  type="button"
                  className="button button--quiet stage__over-skip"
                  title={t('stage.skip-initials-title')}
                  onClick={skipInitials}
                >
                  {t('stage.skip-initials')}
                </button>
              </div>
              <p className="stage__over-note">
                {t('stage.initials-help')}
              </p>
            </div>
          ) : null}

          {/* The end of the funnel (08 §1). The score is stated first and plainly, because it is
              the thing the player earned and the only reason the rest of this is worth reading. */}
          {finished ? (
            <div className="stage__over" role="group" aria-label={t('stage.game-over')}>
              {/*
                The one instant in a game a player wants to screenshot (M3-48).

                **A personal best was being reported as a number and nothing else.** The panel
                already said what the score was and what an account would do with it; it never said
                the thing the player actually just did. 08 §1's argument — *"That is a real score"*
                — works harder when the machine has noticed *why* it is a real score.

                Solo games only. In a hot-seat game the local table is one browser's and "your
                best" is four people's, so the claim would be wrong for at least three of them.
                `role="status"` rather than an alert: it is good news, and it should wait for a gap
                rather than interrupt the callout that is very likely still playing.
              */}
              {personalBest ? (
                <p className="stage__best" role="status">
                  <span aria-hidden="true">★</span> {t('stage.best-yet')}
                </p>
              ) : null}
              <p
                className={personalBest ? 'stage__over-score stage__over-score--best' : 'stage__over-score'}
              >
                {readout.score.toLocaleString()}
              </p>
              {endOffer !== undefined ? (
                <p className="stage__over-line">{t(endOffer.line)}</p>
              ) : null}
              <div className="stage__over-actions">
                <button
                  type="button"
                  className="button button--primary"
                  title={t('stage.play-again-title')}
                  onClick={launch}
                >
                  {t('stage.play-again')}
                </button>
                {/* Disabled and saying why, rather than absent or lying. A control that is not
                    here yet is a real state; a "coming soon" page that never fails is not. This is
                    the branch that ran for months after it had stopped being true — see
                    `endOffer`. */}
                {accountUnavailable !== undefined ? (
                  <button
                    type="button"
                    className="button"
                    disabled
                    title={accountUnavailable}
                  >
                    {t('stage.make-account')}
                  </button>
                ) : endOffer !== undefined ? (
                  <button
                    type="button"
                    className="button"
                    title={t(endOffer.title)}
                    onClick={() => void navigate(endOffer.to)}
                  >
                    {t(endOffer.label)}
                  </button>
                ) : null}
              </div>
              {/*
                The Builder, said once and quietly (M10-30).

                **Under the buttons rather than in them.** It is an action, so it belongs with the
                actions; it is not the action, so it is not a button. Above the local table because
                a line that follows the scores is fine print, and the reader has to be able to see
                it without deciding to.

                The name is `builder.open`, which is what every other door in the client calls
                this place — pricing, settings, the 404, both of `/play`'s dead ends and the
                subscriber's own button four lines above this one. A product that calls one
                destination three things teaches nobody where it is.
              */}
              {buildAside ? (
                <p className="stage__over-aside">
                  {t('stage.build-aside')}{' '}
                  <Link className="stage__over-aside-link" to="/builder" title={t('builder.open-title')}>
                    {t('builder.open')}
                  </Link>
                </p>
              ) : null}
              {/* Kept here, in this browser, with no account and no server record (M3-39). The
                  loss-aversion argument in 08 §1 only works honestly if the score really is
                  theirs already — so it is, before anybody is asked for anything. */}
              {table.length > 0 ? (
                <ol className="stage__table">
                  {table.map((row) => (
                    <li key={`${row.at}`} className="stage__table-row">
                      <span className="stage__table-initials">{row.initials}</span>
                      <span>{row.score.toLocaleString()}</span>
                    </li>
                  ))}
                </ol>
              ) : null}
              {/*
                Where the score went, and — only for somebody who has not signed up — that nothing
                is being asked of them for it.

                **"Until then this machine asks for nothing" is addressed to a visitor**, and it
                was being shown to everybody. To a player with an account there is no "then" left:
                they signed up, their scores already sync, and a machine telling them it will start
                asking for something eventually is a promise of a bill that is not coming. The
                first sentence stays for all three tiers, because the local table is real whoever
                is reading it.
              */}
              <p className="stage__over-note">
                {history?.available === true ? t('stage.kept-here') : t('stage.cannot-keep')}{' '}
                {accountUnavailable ?? ''}{' '}
                {endOffer?.label === 'stage.keep-score' ? t('stage.until-then') : ''}
              </p>
            </div>
          ) : null}

          {/* 08 §1's single primary control. It disappears once the ball is in play, because a
              button labelled "pull the plunger" over a ball that is already moving is the machine
              asking for something it does not want — and, since M3-49, once the cabinet's own
              plunger is on screen and able to do the job. See `plungerOnCabinet`.

              **And while the interrupted game is being offered.** See `resumeOffered`: the card's
              own two buttons are the primary control at that moment, and this one both crowded
              them and answered for them. */}
          {waiting && running && !plungerOnCabinet && !resumeOffered ? (
            <button
              type="button"
              className="button button--primary"
              title={
                myBall === false
                  ? t('stage.their-ball', { name: upNext ?? t('stage.somebody-else') })
                  : t('stage.put-ball')
              }
              // **Disabled when it is somebody else's ball** (M8-17). The seat model refuses an
              // `endBall` from the wrong player anyway, so this is not what makes the rule true —
              // it is what stops a player pulling a plunger that was never going to do anything
              // and concluding the machine is broken.
              disabled={myBall === false}
              {...(myBall === false ? { 'aria-disabled': true } : {})}
              onClick={launch}
            >
              ▸ PULL THE PLUNGER
            </button>
          ) : null}
        </div>
      </div>

      {/*
        The cabinet (M3-49), which is `CabinetBand` — its own file since M9-31, because the
        Builder's test play needs the same two buttons and the same plunger under its own canvas
        and a second copy of them would be two machines within a milestone.
      */}
      <CabinetBand
        bandRef={bandRef}
        layout={layout}
        orientation={orientation}
        bats={bats}
        hinted={showZones}
        teaching={showPull}
        pullWord={t('stage.pull-down')}
      />
      {/*
        An invite that could not be taken up (G3-05). Said rather than swallowed: a player who
        followed a link and got an ordinary solo machine has no way to tell a game that ended from
        a product that ignored them.
      */}
      {inviteRefused ? (
        <p className="notice" role="status">
          {t('stage.invite-gone')}
        </p>
      ) : null}
      {/*
        The getting-started strip used to be here and is now on the settings page (G2-11, M8-77).

        **Not a gate, a move.** It was first gated on focus mode, because focus mode drops the two
        navigation rows and the strip was a third band nobody had asked about. That fixed the
        reported case and left the general one: this is the bottom of the machine page on a phone,
        it is where the cabinet and the flipper band are, and a four-step checklist there is the
        most valuable strip of the screen spent on homework. `Settings` draws it now, in the
        "Getting started" row that already existed to clear it — a row that offered to reset a
        progress it never showed.

        The steps are still *marked* here, by `markOnboarding`: what a player has done is a fact
        about playing, and only the machine knows it.
      */}
    </section>
  )
}

/**
 * What the panel says while somebody else has the ball (M8-19).
 *
 * **Three states and they are genuinely different to a player.** Their ball is on this glass;
 * their ball is on this glass but a moment of it is still arriving; or nothing of it has reached
 * this browser at all and the machine is showing its own attract. Saying "playing" for all three
 * would make the third one — the only one where what is on screen is *not* their game — the one
 * that reads wrong.
 */
export function watchingLine(
  who: string | undefined,
  watching: { readonly gapped: boolean; readonly diverged?: boolean } | undefined,
  t: Translator,
): string {
  const name = who ?? t('stage.somebody')
  if (watching === undefined) return t('stage.is-playing', { name })
  // **Diverged outranks gapped, because wrong outranks late.** `ghostDivergedAt`'s own doc has
  // said since it was written that a divergence here "has not caught anybody cheating — it has
  // caught *us*", and that it is "worth surfacing rather than swallowing" — a claim about a
  // surface that did not exist until this line (M9-53). A gapped picture catches up; a diverged
  // one is a different game wearing the live one's caption, and the player deserves the caption
  // that says so.
  if (watching.diverged === true) return t('stage.watching-diverged', { name })
  return watching.gapped
    ? t('stage.watching-gapped', { name })
    : t('stage.watching-live', { name })
}

/** How long the browser-refused-it line stays up. The same eight seconds the runtime's use. */
const REFUSAL_MS = 8000

/**
 * How long the motion-is-on confirmation stays up when no shove ever arrives.
 *
 * The same eight seconds as the refusal, for the same reason: long enough to read a sentence
 * nobody was expecting, short enough to be gone before it becomes furniture.
 */
const MOTION_HINT_MS = 8000

// `ballOf`, which counted launch events, is gone: the number is recorded beside the trace by
// `LocalHistory.saveInProgress`. Counting could not work. The press that starts a game is a
// launch, so a game still on ball one read as ball two; a ball save puts the ball back in the
// lane for another launch on the same ball; and a plunger pulled twice is two events. The game
// knows which ball it is on, and now so does the record.

/**
 * Two bars when the machine is running, a triangle when it is paused.
 *
 * **The glyph states what a press would do, unlike the label beside it.** That is the one place in
 * this product where the two disagree, and deliberately: every transport control ever built shows
 * a triangle to mean "this will start again", so a paused machine showing bars — the condition —
 * would be read by every player as a button that has not worked yet. The accessible name and the
 * `title` keep the usual rule; the drawing follows the convention players already carry.
 *
 * Geometry rather than the `‖` this replaced: a typed character inherits the font's metrics, sits
 * on the baseline rather than in the middle of a 44 px button, and is a different width in every
 * font a browser might fall back to — the same argument `StageMenu`'s dots are drawn for.
 */
function PausePlayGlyph({ paused }: { readonly paused: boolean }): React.JSX.Element {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {paused ? (
        <path d="M8 5l12 7-12 7z" />
      ) : (
        <>
          <rect x={7} y={5} width={3.5} height={14} rx={1} />
          <rect x={13.5} y={5} width={3.5} height={14} rx={1} />
        </>
      )}
    </svg>
  )
}

/**
 * How far above the bottom of the pane the key guide sits (M3-56).
 *
 * **The drawn cabinet gets its corner back.** The rail's first position was `bottom: 12px`, and on
 * a 1347 × 762 pane that put 122 × 73 px of it over the left flipper bat — a control on a phone,
 * paint on a desktop, and a piece of the machine either way.
 *
 * So the offset comes from the same number the bat itself is drawn at, exactly as `--bat-size` and
 * `--lane` already do: nothing here may be a pixel count that happens to work on one window. The
 * bat is a long shape lying nearly flat, so its drawn box is never taller than it is wide; three
 * quarters of that width clears it with room for the rotation, and the `32` is the zone's own
 * `padding-bottom`, which is what the bat is anchored to.
 *
 * Portrait never asks — the guide is landscape-only — and the fallback keeps it honest if it ever
 * does.
 */
export function keyGuideLift(layout: PhoneLayout, orientation: Orientation): number {
  if (orientation !== 'landscape') return 12
  const bat = batWidthFor(layout.left.width, layout.right.width, layout.plunger.width)
  return Math.round(bat * 0.75) + 32
}

/**
 * The tallest the key guide may be before it reaches the HUD (M3-56).
 *
 * The pane is spoken for at both ends — the HUD's gradient at the top, the drawn cabinet at the
 * bottom — and on a short window there is not enough left over for all five rows. **The rows scroll
 * rather than the rail sliding under something**, which is the same choice `.keyGuide`'s
 * `overflow-y` already makes and the reason it is there.
 *
 * The 12 is the gap the rail keeps from the HUD, matching its own inset from the left edge.
 * Floored at one row's worth: a rail squeezed to nothing would be a control that is present,
 * focusable and impossible to read.
 */
export function keyGuideHeadroom(paneHeight: number, hudHeight: number, lift: number): number {
  return Math.max(64, Math.round(paneHeight - hudHeight - lift - 12))
}

/**
 * The Builder's own route, named once.
 *
 * Two decisions in this file turn on it now, and one of them is "is the other one already sending
 * them here?" — a question a repeated string literal answers correctly right up until somebody
 * changes one of the two.
 */
export const BUILDER_ROUTE = '/builder'

/**
 * What the panel at the end of a game offers, and where it leads (08 §1, 01 §6, M10-16).
 *
 * Exported and tier-in/answer-out so the rule can be tested without a machine, a canvas or a
 * router — the same shape `paywall-placement.test.ts` already checks the other doors with.
 *
 * **Three answers, and the third is `undefined`.** A subscriber is offered nothing: they have the
 * roster, the Builder, the arcade and the headset already, and the only honest thing to put beside
 * "Play again" for them is nothing at all.
 *
 * **A cold tab counts as anonymous, deliberately.** `knownTier` distinguishes "never told" from
 * "told: anonymous", and on the front door the distinction has no content — FR-030 forbids this
 * page from asking, so a visitor who has never signed in is `undefined` for ever. `Arcade` resolves
 * it the same way for the same reason. The cost of being wrong is one signed-in player seeing a
 * free-account offer on a tab that has never loaded the account bundle; the cost of the other
 * reading is every genuine visitor being offered nothing.
 */
export function endOfGameOffer(tier: Tier | undefined, built: boolean): EndOfGameOffer | undefined {
  const known = tier ?? 'anonymous'
  // `false` for the checking flag: nothing is in flight, because nothing on this page may ask.
  if (gateVerdict('cloud-scores', known, false) === 'needs-account') {
    return {
      line: 'stage.real-score',
      label: 'stage.keep-score',
      title: 'stage.keep-score-title',
      to: '/account?want=cloud-scores',
    }
  }
  if (gateVerdict('full-roster', known, false) === 'needs-pro') {
    return {
      line: 'stage.pro-adds',
      label: 'stage.more-machines',
      title: 'stage.more-machines-title',
      to: '/account?want=full-roster',
    }
  }
  /**
   * **The subscriber's door, and it is not an offer** (01 §6, M10-24).
   *
   * This branch used to be the `undefined` below it, with a comment saying that the honest thing to
   * put beside "Play again" for somebody who has the roster, the Builder, the arcade and the
   * headset is nothing at all. That was right about *selling* and wrong about the product: the
   * Builder is half of what a subscription buys and the one half a player can hold for months
   * without ever meeting, because until M10-24 nothing in the client linked to it and the nav tab
   * it lives on is dropped from every phone narrower than 400 CSS pixels.
   *
   * **So it is a destination rather than a `?want=` door**, which is what makes it not an upsell:
   * every other answer here leads to `/account` to resolve an entitlement, and this one leads
   * straight into a workspace this viewer already owns.
   *
   * **And it retires by being taken.** `built` is whether this browser holds a machine of its own,
   * which is the same signal the front door's switch is drawn from — so a subscriber who has made
   * one is back to being offered nothing, permanently, and the panel never nags. A boolean rather
   * than a store read because this function is the decision and has no business opening IndexedDB;
   * `MachineStage` asks, and only for a subscriber.
   */
  if (known === 'pro' && !built) {
    return { line: 'stage.build-line', label: 'builder.open', title: 'builder.open-title', to: BUILDER_ROUTE }
  }
  return undefined
}

/**
 * How many games must end before the panel mentions the Builder to a player who is not paying for
 * it (M10-30).
 *
 * Two, and the second is the first one that is not a stranger's first impression. See the
 * `gamesEnded` comment in the component for the argument; the number is here because it is the part
 * a reader will want to change, and it is exported so the test that pins the rule cannot drift from
 * the rule by asserting a literal of its own.
 */
export const BUILDER_ASIDE_AFTER = 2

/**
 * Whether the end of a game also says, quietly, that the Builder is there (M10-30).
 *
 * **A door rather than an offer, which is the whole reason it may be shown to a visitor.** 01 §6's
 * rule is that nobody is quoted a price for something free, and the Builder is free to open, free to
 * design in and free to test-play; a draft has survived a reload since M9-32. What a subscription
 * buys is the machine leaving this browser. So the aside leads into the tool, and `endOfGameOffer`'s
 * two entitlement doors are left exactly as they were — the score a visitor just earned is the
 * strongest thing on this panel and it keeps the button.
 *
 * **Three ways it stays quiet.** It is not offered to a browser that already holds a machine; it is
 * not offered on the first game-over panel this browser has ever shown; and it is not offered to
 * the one tier whose *primary* offer is already the Builder — which is asked of `endOfGameOffer`
 * rather than re-derived here, because two copies of a tier rule are two rules within a milestone.
 *
 * **The middle one takes two signals, and shipping with one of them was the defect** (M10-31).
 * `gamesEnded` counts this mount; `playedBefore` is a score already on the local table. The rule is
 * about the *browser*, and only the second survives a reload — with the counter alone, a player who
 * loaded the page for each game stayed at one for ever. Either is enough, because each covers what
 * the other cannot: a reload erases the count, and a game that scored nothing writes no row.
 */
export function builderAsideOffered(
  tier: Tier | undefined,
  built: boolean,
  gamesEnded: number,
  playedBefore: boolean,
): boolean {
  if (built) return false
  if (gamesEnded < BUILDER_ASIDE_AFTER && !playedBefore) return false
  return endOfGameOffer(tier, built)?.to !== BUILDER_ROUTE
}

/** One offer: the sentence above the buttons, the button, and the door it opens. */
export interface EndOfGameOffer {
  readonly line: MessageKey
  readonly label: MessageKey
  readonly title: MessageKey
  /**
   * Where the button goes.
   *
   * The `?want=` convention `EnterHeadset` and the arcade's locked tiles already use, for the two
   * answers that are about an entitlement — **and a plain route for the one that is not.** A
   * subscriber sent to `/account?want=builder-private` would be shown a price for something they
   * are already paying for, which is the dishonest paywall this whole file argues against.
   */
  readonly to: string
}
